import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Square, Pause, RotateCcw, Shuffle, Settings2, Users, Upload, UserPlus, Zap, Edit2, Bell, BellOff, Check, X, MoreVertical, ShieldCheck, Megaphone, QrCode, ChevronDown, AlertOctagon, ShoppingCart } from "lucide-react";
import NotificationsPanel from "@/components/NotificationsPanel";
import { QRCodeSVG } from "qrcode.react";
import { CoachingSetupModal, ScheduledCoachingPanel } from "../components/CoachingSetupModal";
import api from "@/lib/api";
import { toast } from "sonner";
import HelperPassQuickModal from "@/components/HelperPassQuickModal";
import SponsorRibbonModal from "@/components/SponsorRibbonModal";
import SkillRangesModal from "@/components/SkillRangesModal";
import { installOpsToastGate, isAlertsOff, setAlertsOff } from "@/lib/opsToastGate";
import { useFestivalReadOnly } from "@/lib/useFestivalReadOnly";
import FestivalEndedBanner from "@/components/FestivalEndedBanner";
import { CourtModeToggle } from "@/components/CourtModeToggle";
import { AssignPartnerModal, canPairPlayer, groupedPlayerIds } from "@/components/AssignPartnerModal";

// Iter305 — Install the sonner success-toast gate as a side effect of
// loading the OPS view. Idempotent — safe if the module reloads.
installOpsToastGate();

/*
 * Iter183 — Festival Live Controller (rebuild).
 *  • Left: signed-up players + active/inactive toggle + right-click menu.
 *  • Center: draggable court cards with drop targets on current + next_up.
 *  • Header: DUPR/Band rating toggle, All-In/Balanced mode, active count.
 *  • Realtime: polls every 3s (viewer picks up changes on next poll).
 */
const STATE_STYLES = {
  idle: "border-[#525252] text-[#A3A3A3] bg-[#0A0A0A]",
  waiting: "border-[#66C2FF] text-white bg-[#0A0A0A]",
  ready: "border-[#CCFF00] text-[#CCFF00] bg-[#0F0F0A]",
  live: "border-[#FF4500] text-white bg-[#4B0F0F]",
  finished: "border-[#FFB020] text-[#FFB020] bg-[#4B3F0F] animate-pulse",
  paused: "border-[#525252] text-[#525252] bg-[#141414]",
  // Legacy aliases (pre-Iter185 rows may still surface these briefly).
  assigned: "border-[#66C2FF] text-white bg-[#0A0A0A]",
  pre_start: "border-[#CCFF00] text-[#CCFF00] bg-[#0F0F0A]",
  timeout: "border-[#FFB020] text-[#FFB020] bg-[#4B3F0F] animate-pulse",
};

const BAND_STYLE = {
  green: "bg-[#0F4B0F] text-[#4CAF50] border-[#4CAF50]",
  blue: "bg-[#0F2A4B] text-[#66C2FF] border-[#66C2FF]",
  red: "bg-[#4B0F0F] text-[#FF4500] border-[#FF4500]",
  unrated: "bg-[#141414] text-[#525252] border-[#262626]",
};

const STATUS_LABELS = {
  // Iter205 — labels only (DB values unchanged). Live Ops still uses
  // `statusHint()` for the dynamic "On Court C1" / "Next Up on C2" text.
  imported: "Awaiting Check-In", arrived: "Checked-In", available: "Awaiting Court",
  assigned: "Next Up", on_court: "On Court", resting: "Resting",
  sin_bin: "Sin-Bin", inactive: "Inactive",
};

const fmt = (s) => {
  if (s == null) return "--:--";
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

// Iter316 — Colour lookup helper. Build a `{range_id → colour_hex}` map
// from the festival's configured `skill_ranges`, then merge legacy names
// on top so festivals still on the pre-iter315 default (green/blue/red)
// keep rendering. Callers pass the resulting map to `RatingChip` / to
// the registration-row swatch so switching a preset (which changes the
// range ids to things like `beginner`/`intermediate`) doesn't blank
// every dot on the OPS console.
const LEGACY_BAND_COLOURS = {
  blue: "#66C2FF",
  green: "#CCFF00",
  red: "#FF4500",
  yellow: "#FFB020",
};
function buildBandColourMap(skillRanges) {
  const m = { ...LEGACY_BAND_COLOURS };
  for (const r of skillRanges || []) {
    if (r?.id && r?.colour_hex) m[String(r.id).toLowerCase()] = r.colour_hex;
  }
  return m;
}
function bandColourFor(map, band) {
  return map[String(band || "").toLowerCase()] || "#525252";
}

// Small chip that renders EITHER a DUPR number or the colour band pill.
function RatingChip({ p, mode, colours }) {
  if (mode === "dupr") {
    return (
      <span className="text-[10px] font-mono px-1.5 py-0.5 border border-[#262626] text-[#A3A3A3]" data-testid={`rating-dupr-${p.id}`}>
        {p.current_rating ?? "—"}
      </span>
    );
  }
  // Iter316 — Colour derived from the festival's active skill_ranges via
  // `colours` prop; legacy default names (green/blue/red/yellow) still
  // resolve via LEGACY_BAND_COLOURS baked into the map.
  return (
    <span
      data-testid={`rating-band-${p.id}`}
      className="inline-block w-3 h-3"
      style={{ backgroundColor: bandColourFor(colours || LEGACY_BAND_COLOURS, p.colour_band) }}
      title={(p.colour_band || "unrated").toUpperCase()}
    />
  );
}

export default function FestivalLiveOps() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Iter389 · Feature 1 — Owner Live Ops exit hook. When the entry
  // sessionStorage flag is set for this festival, POST /owner-live-ops
  // {exit} on unmount and beforeunload so the organiser + deputy get
  // the "Owner has left" notification.
  useEffect(() => {
    const flagKey = "ownerLiveOpsFestivalId";
    const stored = sessionStorage.getItem(flagKey);
    if (stored !== id) return undefined;
    const fireExit = () => {
      try {
        const url = `${(process.env.REACT_APP_BACKEND_URL || "")}/api/admin/festival/${id}/owner-live-ops`;
        const token = document.cookie.split("; ").find((c) => c.startsWith("access_token="))?.split("=")[1];
        const body = JSON.stringify({ action: "exit" });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        } else if (token) {
          fetch(url, { method: "POST", body, credentials: "include",
                        headers: { "Content-Type": "application/json" } });
        }
      } catch {}
    };
    window.addEventListener("beforeunload", fireExit);
    return () => {
      window.removeEventListener("beforeunload", fireExit);
      // React-side navigation away also fires — clear the flag and
      // POST exit so the notification is delivered.
      import("@/lib/api").then(({ default: api }) => {
        api.post(`/admin/festival/${id}/owner-live-ops`, { action: "exit" }).catch(() => {});
      });
      try { sessionStorage.removeItem(flagKey); } catch {}
    };
  }, [id]);
  const [state, setState] = useState(null);
  const [festival, setFestival] = useState(null);
  const [tick, setTick] = useState(0);
  // Iter257 — Track when the last successful poll returned so we can
  // compute the exact elapsed seconds between polls. Previously the
  // timer used `tick % 3` which wraps every 3s and made the clock
  // bounce MM:SS → MM:SS-1 → MM:SS-2 → MM:SS → … instead of actually
  // counting down — most noticeable on the 5-minute pre-start clock.
  const lastPollAtRef = useRef(Date.now());
  const [menu, setMenu] = useState(null); // {player, x, y}
  // Iter347 · Fix 1 — Collapse the "Assign to Coaching" and "Move to
  // court" submenus by default so the right-click menu stays compact
  // on busy events. Both reset to false on every openMenu call.
  const [menuSub, setMenuSub] = useState({ coaching: false, moveTo: false });
  const [dragPid, setDragPid] = useState(null);
  // Iter327 · Bug 1 — Drag activity ref. Pausing the 3-second poll
  // during an active drag prevents React from remounting the drag
  // source element mid-operation, which is the root cause of the
  // "locked cursor + nothing clickable" freeze (HTML5 DND leaves the
  // pointer stuck in `grabbing` state when its origin element is
  // torn down by a background state swap). We also fire a document-
  // level `dragend` listener to clear the state if the user aborts
  // the drag by pressing Escape or dropping outside a valid target.
  const dragActiveRef = useRef(false);
  // Iter295 — OPS Cols/Row control (independent of the viewer setting).
  // 0 = Auto (uses the previous responsive breakpoints).
  const [opsCols, setOpsCols] = useState(() => {
    try {
      const raw = window.localStorage.getItem(`rl_ops_cpr_${id}`);
      const n = Number(raw);
      if (n >= 1 && n <= 6) return n;
    } catch {}
    return 0;
  });
  const setOpsColsPersist = (n) => {
    const v = Number(n) || 0;
    const clamped = v === 0 ? 0 : Math.max(1, Math.min(6, v));
    setOpsCols(clamped);
    try { window.localStorage.setItem(`rl_ops_cpr_${id}`, String(clamped)); } catch {}
  };
  // Iter285 — Swap-out modal state. When admin drops onto a full
  // court we ask "swap a player out?"; if Yes we render the existing
  // roster and let admin pick who to remove. On selection we hit
  // POST /courts/{id}/swap-player.
  const [swapModal, setSwapModal] = useState(null); // {courtId, slot, incomingPid, existingPids, label}
  // Iter184 — quick "Add Player" walk-in modal + inline name/rating edit modal.
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // player being name/rating edited
  // Iter191 — Admin Pending Requests tray (desk-staff proxy accept flow).
  const [pending, setPending] = useState([]);
  const [showTray, setShowTray] = useState(false);
  // Iter274 — Coaching Setup Modal state. Opens automatically when the
  // admin flips a court to reserved_coaching, or via Edit on the panel.
  const [coachingModalCourt, setCoachingModalCourt] = useState(null);
  // Iter315 — Skill Ranges editor modal open state.
  const [skillRangesOpen, setSkillRangesOpen] = useState(false);
  // Iter316 — Locked-group aware play requests + drag-confirm modal.
  // `playRequests` is populated by /play-requests each poll so onDrop
  // can detect a locked-group member being dragged onto a foreign court.
  const [playRequests, setPlayRequests] = useState([]);
  // Modal shape: {courtId, slot, playerId, request} — open when a drag
  // would break a locked group. Three-option resolution below.
  const [lockedDragModal, setLockedDragModal] = useState(null);
  const [partnerFor, setPartnerFor] = useState(null);

  // Iter316 — Colour lookup rebuilt from the festival's live
  // `skill_ranges`. Ensures the OPS dots track the currently-active
  // preset instead of freezing on legacy green/blue/red names.
  const bandColourMap = useMemo(
    () => buildBandColourMap(festival?.skill_ranges),
    [festival?.skill_ranges],
  );
  const busy = useRef(false);
  // Iter192 — long-press for touch devices (defined up top to keep hook
  // order stable across the early-return below).
  const longPressRef = useRef({ timer: null, moved: false });
  // Iter201 — Playing-tab filter state.
  const [playingSearch, setPlayingSearch] = useState("");
  const [playingSort, setPlayingSort] = useState("court");
  // Iter203 — Registration section: search + expand toggle. And a
  // scroll-preservation ref so a check-in doesn't yank the admin's
  // scroll position when the row jumps sections.
  const [regSearch, setRegSearch] = useState("");
  const [regExpanded, setRegExpanded] = useState(true);
  // Iter315 — Restored Filter (by status) + Sort (by name/rating/status).
  // These existed in earlier iterations, got dropped in the sidebar
  // clean-up, and are now back per user spec.
  const [regFilter, setRegFilter] = useState("all");
  const [regSort, setRegSort] = useState("az");
  // Iter315 — Resizable OPS sidebar (width + height persisted). Width
  // drags on the right edge; height on the bottom edge. The court grid
  // reflows into whatever's left because the sidebar is a fixed column.
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const s = Number(window.localStorage?.getItem("ops-sidebar-width"));
    return Number.isFinite(s) && s >= 240 && s <= 720 ? s : 320;
  });
  const [sidebarHeight, setSidebarHeight] = useState(() => {
    const s = Number(window.localStorage?.getItem("ops-sidebar-height"));
    return Number.isFinite(s) && s >= 280 ? s : 0;  // 0 = auto (full)
  });
  const asideRef = useRef(null);
  const savedScrollRef = useRef(0);
  // Iter224 — Desk Mode. Helpers (added by the owner via /helpers) see
  // a stripped console: no settings rail, no Quick Actions panel.
  const [role, setRole] = useState({ is_owner: true, is_helper: false, role: "owner" });
  // Iter235 — canGenerateHelpers = owner OR festival_only_helper.
  const canGenerateHelpers = role.role === "owner" || role.role === "festival_only_helper" || role.role === "deputy_helper" || (role.is_owner && !role.role);
  const [showHelperQuick, setShowHelperQuick] = useState(false);
  const [showSponsorsModal, setShowSponsorsModal] = useState(false);
  // Iter305 — QR popover next to the External View button (renders a
  // scannable QR pointing at the public viewer URL). Anchor state is
  // just a boolean; the popover positions itself relative to its
  // wrapping <div> which acts as the anchor.
  const [showViewerQr, setShowViewerQr] = useState(false);
  // Iter305 — Alerts toggle. sessionStorage-backed via opsToastGate.js
  // so the flip persists across page reloads within the tab.
  const [alertsOff, setAlertsOffState] = useState(() => isAlertsOff());
  const toggleAlerts = () => {
    const next = !alertsOff;
    setAlertsOff(next);
    setAlertsOffState(next);
  };
  useEffect(() => {
    let alive = true;
    api.get(`/festival/${id}/my-role`).then((r) => {
      if (alive) setRole(r.data || { is_owner: true, is_helper: false });
    }).catch(() => {});
    return () => { alive = false; };
  }, [id]);
  const isHelper = role.is_helper && !role.is_owner;
  // Iter462 — Shared post-festival read-only gate. Reuses the same
  // logic Iter461 introduced in Player Manager so every mutation
  // surface on Live Ops locks identically once `festival.end_at`
  // has passed. Platform admins retain full access.
  const { isReadOnly } = useFestivalReadOnly(festival);
  const blockWrite = () => {
    if (isReadOnly) {
      toast.info("This festival has ended. Please request an extension to make changes.");
      return true;
    }
    return false;
  };
  const toggleFullscreen = () => {
    try {
      if (!document.fullscreenElement) document.documentElement.requestFullscreen();
      else document.exitFullscreen();
    } catch {}
  };
  // Iter237c — Reverted the Iter226/232 split-panel/Rnd system entirely
  // (it fought the grid + parent positioning and drag broke). The docked
  // 3-column view is the only view. Panels always sit to the side of
  // the courts. Iter237c also lets the OWNER hide the app chrome
  // (sidebar + top nav) via a body class so 8+ courts fit at 100%.
  const [chromeHidden, setChromeHidden] = useState(() => {
    try { return window.localStorage.getItem(`rl_ops_chrome_hidden_${id}`) === "1"; } catch { return false; }
  });
  useEffect(() => {
    document.body.classList.toggle("rl-ops-chrome-hidden", chromeHidden);
    return () => document.body.classList.remove("rl-ops-chrome-hidden");
  }, [chromeHidden]);
  const toggleChrome = () => {
    setChromeHidden((v) => {
      const next = !v;
      try { window.localStorage.setItem(`rl_ops_chrome_hidden_${id}`, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  // Iter237c — Per-button visibility toggles for Quick Actions. Owner
  // can hide any of the five bulk-action buttons if they never use them
  // (e.g. "Shuffle Next Up", "Pause All"). Persisted per-festival.
  const QA_BUTTONS = ["generate", "assign", "shuffle", "pause", "resume"];
  const [qaVisible, setQaVisible] = useState(() => {
    try {
      const raw = window.localStorage.getItem(`rl_ops_qa_visible_${id}`);
      if (raw) return { ...Object.fromEntries(QA_BUTTONS.map((k) => [k, true])), ...JSON.parse(raw) };
    } catch {}
    return Object.fromEntries(QA_BUTTONS.map((k) => [k, true]));
  });
  const [qaEditing, setQaEditing] = useState(false);
  const toggleQa = (key) => setQaVisible((v) => {
    const next = { ...v, [key]: !v[key] };
    try { window.localStorage.setItem(`rl_ops_qa_visible_${id}`, JSON.stringify(next)); } catch {}
    return next;
  });
  // Iter245 — Collapsible sections. Each of the four ops panels
  // (Registration, Signed-Up, Playing, Quick Actions) has its own
  // expanded/collapsed state, persisted per-festival so the admin
  // gets back the layout they left last time. When collapsed the
  // header still shows a live count so the desk can see queue depth
  // at a glance without expanding.
  const [signedUpExpanded, setSignedUpExpanded] = useState(() => {
    try { return window.localStorage.getItem(`rl_ops_signed_up_expanded_${id}`) !== "0"; } catch { return true; }
  });
  const toggleSignedUp = () => setSignedUpExpanded((v) => {
    const next = !v;
    try { window.localStorage.setItem(`rl_ops_signed_up_expanded_${id}`, next ? "1" : "0"); } catch {}
    return next;
  });
  const [qaExpanded, setQaExpanded] = useState(() => {
    try { return window.localStorage.getItem(`rl_ops_qa_expanded_${id}`) !== "0"; } catch { return true; }
  });
  const toggleQaSection = () => setQaExpanded((v) => {
    const next = !v;
    try { window.localStorage.setItem(`rl_ops_qa_expanded_${id}`, next ? "1" : "0"); } catch {}
    return next;
  });
  // Iter205 — buzzer chime on ready→live transition. Web-Audio synth so
  // there's no audio asset to ship. Muted by default until the admin
  // taps anywhere (browser autoplay policy) OR toggles Sound on.
  const prevCourtStatesRef = useRef({});
  const audioCtxRef = useRef(null);
  const [chimeEnabled, setChimeEnabled] = useState(() => {
    try { return window.localStorage.getItem("rl_ops_chime") !== "0"; } catch { return true; }
  });
  const toggleChime = () => setChimeEnabled((v) => {
    const next = !v;
    try { window.localStorage.setItem("rl_ops_chime", next ? "1" : "0"); } catch {}
    if (next) playChime(true); // preview beep
    return next;
  });
  const playChime = (preview = false) => {
    if (!chimeEnabled && !preview) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      // Two-tone ding: 880Hz → 1320Hz.
      const now = ctx.currentTime;
      [{ f: 880, at: 0, len: 0.18 }, { f: 1320, at: 0.16, len: 0.24 }].forEach(({ f, at, len }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = f;
        gain.gain.setValueAtTime(0.0001, now + at);
        gain.gain.exponentialRampToValueAtTime(0.35, now + at + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + len);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + at);
        osc.stop(now + at + len + 0.02);
      });
    } catch {}
  };

  // Iter321 — Over-cap soft-block state. Backend returns 402 on
  // /courts when the paid tier's player_cap is exceeded. We display a
  // persistent banner + in-page upgrade modal instead of looping
  // toasts or a stuck spinner.
  const [overCap, setOverCap] = useState(null); // {detail, count, cap, tier}
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  // Iter326 · Bug 1 — Live Ops opens N hours before the event's local
  // start (default 25h, configurable per festival). Before that
  // window the /courts endpoint returns 403 with a structured payload
  // (`{message, live_ops_locked_until, early_access_hours}`). Court
  // OPS + Deputy helper redemptions land here, so we surface a
  // friendly lock screen instead of the raw AxiosError.
  const [liveLocked, setLiveLocked] = useState(null);

  const load = async () => {
    // Iter203 — capture aside scroll BEFORE state swaps in so the layout
    // effect below can restore it after React re-renders.
    if (asideRef.current) savedScrollRef.current = asideRef.current.scrollTop;
    // Iter326 · Bug 1 — Wrap /courts in a granular catch so a 403
    // (early-access lock) doesn't crash the whole page for helper
    // sessions. On lock we still fetch the festival for the header +
    // render a friendly banner.
    const cResP = api.get(`/festival/${id}/courts`, { __suppress402: true })
      .catch((e) => ({ __err: e }));
    let c, f, pr, plg;
    try {
      [c, f, pr, plg] = await Promise.all([
        // Iter320/321 — Suppress the global 402 toast; the page renders
        // its own persistent over-cap banner instead of a looping
        // "Roster over cap" message every 3s poll.
        cResP,
        api.get(`/festival/${id}`),
        api.get(`/festival/${id}/partner-requests`).catch((e) => {
          // Iter329 · Bug 2a — Surface network errors in the console
          // instead of swallowing them into an empty tray. Silent
          // fallback was masking legitimate failures (403 for shifting
          // helper roles, 500 blips) so the badge sat at 0 forever.
          if (e?.response?.status) {
            console.warn(`[FestivalLiveOps] partner-requests fetch failed ${e.response.status}`, e.response?.data);
          }
          return { data: { items: [] } };
        }),
        api.get(`/festival/${id}/play-requests`).catch(() => ({ data: { requests: [] } })),
      ]);
    } catch (e) {
      // Iter448 — Helper-PIN single-device kickout. The api.js
      // interceptor is already firing the amber toast and the hard
      // redirect; swallow the rejection here so no page-level
      // "runtime error" / red toast flashes while the redirect
      // lands. Any other error re-throws so the existing error
      // UX (empty state, 402 banner, etc.) still kicks in.
      if (e?.__helperKickout) return;
      throw e;
    }
    // Iter329 · Bug 2a — Refresh the pending tray on EVERY poll,
    // regardless of the /courts branch. Previously setPending was
    // only invoked in the happy path (line 425), so any 402 (over-
    // cap) or 403 (early-access lock) permanently blanked the panel
    // even though `pr` had valid data. Moved here so both the
    // over-cap banner + the pending tray refresh together.
    setPending(pr.data.items || []);
    setPlayRequests(plg.data?.requests || []);
    if (c && c.__err) {
      // Iter448 — If the /courts catch intercepted a helper-PIN
      // kickout, the api.js interceptor is already running its
      // amber toast + hard redirect. Return silently so the stale
      // page doesn't surface a "runtime error" before unload.
      if (c.__err?.__helperKickout) return;
      if (c.__err?.response?.status === 403) {
        const d = c.__err.response.data?.detail;
        const locked = typeof d === "object" && d
          ? d
          : { message: (typeof d === "string" && d) || "Live Ops is locked for this festival." };
        setLiveLocked(locked);
        setFestival(f.data);
        setState((prev) => prev || { courts: [], players: {}, config: f.data?.config || {} });
        lastPollAtRef.current = Date.now();
        return;
      }
      throw c.__err;
    }
    setLiveLocked(null);
    // Iter321 — 402 envelope → over-cap soft block. Skip payload
    // consumption + keep the last-known good state so the operator
    // can still see the current roster / court layout underneath.
    if (c?.__handled402) {
      // Iter327 · Bug 7 — Backend now returns structured 402 detail
      // (`{message, count, cap, tier, pass_type}`) so we can filter
      // the delta-upgrade modal to only tiers whose cap actually
      // covers the current roster. String-only fallback preserved
      // for pre-iter327 backends.
      const d = c.__detail;
      if (d && typeof d === "object") {
        setOverCap({ detail: d.message || "Roster over cap for your tier.", ...d });
      } else {
        setOverCap({ detail: d || "Roster over cap for your tier." });
      }
      setFestival(f.data);
      // If we've never loaded state, seed with an empty shell so the
      // rest of the page renders (no infinite spinner). Otherwise
      // leave the last good state visible.
      setState((prev) => prev || { courts: [], players: {}, config: f.data?.config || {} });
      lastPollAtRef.current = Date.now();
      return;
    }
    setOverCap(null);
    // Iter205 — buzzer chime when any court transitions ready→live.
    try {
      const prev = prevCourtStatesRef.current || {};
      const now = {};
      for (const court of (c.data?.courts || [])) {
        now[court.id] = court.state;
        if (prev[court.id] === "ready" && court.state === "live") {
          playChime();
        }
      }
      prevCourtStatesRef.current = now;
    } catch {}
    setState(c.data);
    setFestival(f.data);
    // Iter329 · Bug 2a — setPending / setPlayRequests already fired
    // above (before the 402/403 branches) so the tray refreshes on
    // every poll regardless of the /courts outcome.
    lastPollAtRef.current = Date.now();
  };

  // Iter321 — Lazy-load pricing when the organiser opens the upgrade
  // modal (avoids a wasted fetch when the roster is under cap).
  const openUpgradeModal = async () => {
    setUpgradeOpen(true);
    if (pricing) return;
    try {
      const r = await api.get(`/festival/pricing`);
      setPricing(r.data?.pricing || null);
    } catch {}
  };

  // Iter321 — Post to basket-checkout for the chosen upgrade tier and
  // send the organiser to Stripe. The webhook (or polling on return)
  // flips the license to `active_paid` with the new pass_type so the
  // cap gate unlocks. Delta cost is displayed but Stripe charges the
  // full new-tier amount (Rally-Lab manually refunds the current
  // license via the admin refund flow).
  const upgradeToTier = async (passType) => {
    if (upgradeBusy) return;
    setUpgradeBusy(true);
    try {
      const { data } = await api.post(`/festival-basket/checkout`, {
        items: [{ pass_type: passType, qty: 1 }],
        festival_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't start checkout";
      toast.error(detail, { duration: 6000 });
      setUpgradeBusy(false);
    }
  };
  useEffect(() => { load().catch(() => { /* iter448 — kickout handled by interceptor */ }); }, [id]);
  // Iter332 · Bug 18 — Helper back-button guard. When the user is
  // running as a Court OPS / Deputy helper (no Rally-Lab account),
  // pressing the browser Back button used to land on the /login
  // wall (previous history entry from the QR scan flow). We push a
  // duplicate history state on mount and re-push on every popstate
  // so Back becomes a no-op — the helper stays inside the festival.
  useEffect(() => {
    if (!isHelper) return;
    const guard = () => {
      try { window.history.pushState(null, "", window.location.href); } catch {}
    };
    guard();
    window.addEventListener("popstate", guard);
    return () => window.removeEventListener("popstate", guard);
  }, [isHelper]);
  useEffect(() => {
    // Iter327 · Bug 1 — Skip polling while a drag is in flight. A
    // remount mid-drag freezes the browser cursor.
    // Iter448 — Wrap each poll tick in a catch so a helper-PIN
    // kickout rejection doesn't surface as an unhandled rejection
    // / "runtime error" banner between the toast and the redirect.
    const poll = setInterval(() => {
      if (dragActiveRef.current) return;
      load().catch(() => { /* kickout + transient errors handled downstream */ });
    }, 3000);
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    const dismiss = () => setMenu(null);
    const globalDragEnd = () => {
      if (dragActiveRef.current) {
        dragActiveRef.current = false;
        setDragPid(null);
      }
    };
    window.addEventListener("click", dismiss);
    window.addEventListener("dragend", globalDragEnd);
    window.addEventListener("drop", globalDragEnd);
    return () => {
      clearInterval(poll); clearInterval(t);
      window.removeEventListener("click", dismiss);
      window.removeEventListener("dragend", globalDragEnd);
      window.removeEventListener("drop", globalDragEnd);
    };
  }, [id]);
  // Iter203 — restore the sidebar scroll so a check-in click doesn't
  // yank the admin's view back to the top.
  useLayoutEffect(() => {
    if (asideRef.current && savedScrollRef.current) {
      asideRef.current.scrollTop = savedScrollRef.current;
    }
  }, [state]);

  const call = async (fn, msg) => {
    if (blockWrite()) return;
    if (busy.current) return; busy.current = true;
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) {
      // Iter448 — Helper-PIN single-device kickout. The api.js
      // interceptor is firing the amber toast + hard redirect
      // right now; do NOT render a red "[401] You have been
      // signed out…" error toast on top of it. Returning silently
      // leaves the clean amber notification as the only thing the
      // helper sees before the page unloads.
      if (e?.__helperKickout) return;
      // Iter209 — allow inner handlers to short-circuit with a sentinel
      // (so hint toasts fire without a duplicate "Failed" error).
      if (e?.message === "__handled__") { await load(); }
      else {
        // Iter267 — Loud, long-lived error toasts so the desk can read
        // the real cause of a failed assign / place / start. Previously
        // the default 3s toast disappeared before the operator's eyes
        // reached it, making bugs feel like silent failures.
        const detail = e?.response?.data?.detail || e?.message || "Failed";
        const status = e?.response?.status;
        const prefix = status ? `[${status}] ` : "";
        toast.error(`${prefix}${detail}`, { duration: 8000 });
      }
    }
    finally { busy.current = false; }
  };

  if (!state || !festival) return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;

  // Iter321 — Over-cap soft block. Render a persistent banner + let
  // the organiser upgrade without leaving the page. Do this AFTER the
  // loading guard so we always have `festival` in scope, and BEFORE
  // the main court grid so operators see the block prominently.
  const OverCapBanner = overCap ? (
    <div
      className="border-2 border-[#FF4500] bg-[#4B0F0F]/60 p-4 mb-4"
      data-testid="live-ops-over-cap-banner"
    >
      <div className="flex items-start gap-3">
        <AlertOctagon className="w-6 h-6 text-[#FF4500] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="label-mono text-[#FF4500] text-[10px]">
            LIVE OPS BLOCKED · ROSTER OVER CAP
          </div>
          <div className="text-sm text-white mt-1">{overCap.detail || overCap.message}</div>
          {overCap.count != null && overCap.cap != null && (
            <div className="text-[10px] uppercase tracking-widest text-[#FFB020] mt-1" data-testid="live-ops-over-cap-numbers">
              {overCap.count} players · cap {overCap.cap}
            </div>
          )}
          <p className="text-xs text-[#A3A3A3] mt-2 leading-relaxed">
            Live Ops stays locked until you upgrade or trim the roster.
            You'll only pay the delta between tiers — your current
            license credit is applied automatically.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openUpgradeModal}
              data-testid="live-ops-open-upgrade"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white"
            >
              <ShoppingCart className="w-4 h-4" /> Choose upgrade
            </button>
            <button
              type="button"
              onClick={() => navigate(`/festival/${id}/players`)}
              data-testid="live-ops-manage-roster"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white"
            >
              Manage roster
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const UpgradeModal = upgradeOpen ? (
    <div
      className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4"
      onClick={() => !upgradeBusy && setUpgradeOpen(false)}
      data-testid="live-ops-upgrade-modal"
    >
      <div
        className="w-full max-w-lg bg-[#0A0A0A] border-2 border-[#CCFF00] p-5 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="label-mono text-[#CCFF00] text-[10px]">
              UPGRADE · DELTA COST
            </div>
            <h2 className="h-display text-xl mt-1">Bump your tier to fit the roster</h2>
            <p className="text-xs text-[#A3A3A3] mt-1">
              Current tier can't hold your player count. Pick a tier
              that covers everyone — pay the delta only.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setUpgradeOpen(false)}
            data-testid="live-ops-upgrade-close"
            className="text-[#A3A3A3] hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {!pricing ? (
          <div className="text-sm text-[#A3A3A3] py-6 text-center">Loading tiers…</div>
        ) : (() => {
          const currentPassType = festival?.license?.pass_type || "";
          const currentDef = pricing[currentPassType] || {};
          const currentAmount = Number(currentDef.amount) || 0;
          const currentDays = Number(currentDef.days) || 1;
          // Iter321 — Show all tiers whose player_cap accommodates the
          // current roster (or unlimited) AND whose day-count is at
          // least the current license's, plus non-POA and different
          // from current. Sort by amount ascending — cheapest fit first.
          const options = Object.entries(pricing)
            .filter(([k, v]) => (v.tier || "") !== "bolton")
            .filter(([k, v]) => k !== currentPassType)
            .filter(([k, v]) => !v.poa)
            .filter(([k, v]) => Number(v.days || 1) >= currentDays)
            .filter(([k, v]) => v.player_cap == null || v.player_cap >= (overCap?.count || 0))
            .sort((a, b) => (Number(a[1].amount) || 0) - (Number(b[1].amount) || 0));
          if (options.length === 0) {
            return (
              <div className="text-sm text-white border border-[#FFB020] bg-[#1A140A] p-3">
                No self-serve tier fits. Contact Rally-Lab for a bespoke package.
              </div>
            );
          }
          return (
            <div className="space-y-2" data-testid="live-ops-upgrade-options">
              {options.map(([k, v]) => {
                const delta = Math.max(0, (Number(v.amount) || 0) - currentAmount);
                const currency = (v.currency || "gbp").toUpperCase();
                return (
                  <button
                    key={k}
                    type="button"
                    disabled={upgradeBusy}
                    onClick={() => upgradeToTier(k)}
                    data-testid={`live-ops-upgrade-${k}`}
                    className="w-full text-left border border-[#262626] hover:border-[#CCFF00] p-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-white truncate">{v.label || k}</div>
                        <div className="text-[10px] text-[#A3A3A3]">
                          Cap {v.player_cap == null ? "∞" : v.player_cap} · {v.days || 1}-day
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-widest text-[#66C2FF]">Pay delta</div>
                        <div className="text-lg font-bold text-[#CCFF00]">
                          +{currency} {delta.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  ) : null;
  const { courts, players, config } = state;
  const ratingMode = config.rating_display || "band";

  // Flatten players from status buckets into one lookup and one signed-up list.
  const allPlayers = Object.values(players).flat();
  const byId = Object.fromEntries(allPlayers.map((p) => [p.id, p]));

  // Iter217 — Team-of-N chip. Build undirected graph of mutual
  // preferred_partner_ids and label every player with the size of
  // their connected component. Only shows a chip when size >= 2.
  const teamSizeById = (() => {
    const adj = {};
    for (const p of allPlayers) adj[p.id] = new Set();
    for (const p of allPlayers) {
      for (const other of (p.preferred_partner_ids || [])) {
        const q = byId[other];
        if (!q) continue;
        if ((q.preferred_partner_ids || []).includes(p.id)) {
          adj[p.id].add(other);
          adj[other].add(p.id);
        }
      }
    }
    const seen = new Set(); const size = {};
    for (const p of allPlayers) {
      if (seen.has(p.id)) continue;
      const stack = [p.id]; const comp = [];
      while (stack.length) {
        const cur = stack.pop();
        if (seen.has(cur)) continue;
        seen.add(cur); comp.push(cur);
        for (const nb of adj[cur] || []) if (!seen.has(nb)) stack.push(nb);
      }
      for (const cid of comp) size[cid] = comp.length;
    }
    return size;
  })();
  const TeamChip = ({ id: pid }) => {
    const n = teamSizeById[pid] || 1;
    if (n < 2) return null;
    return (
      <span
        className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#66C2FF] text-[#66C2FF]"
        data-testid={`team-chip-${pid}`}
        title={`Requested to play with ${n - 1} other player${n === 2 ? "" : "s"}`}
      >
        Team of {n}
      </span>
    );
  };
  // Iter221 — colour-coded chip per party_id so the desk can spot bundles.
  const partySizeById = (() => {
    const size = {};
    const groups = {};
    for (const p of allPlayers) {
      const pid = p.party_id;
      if (!pid) continue;
      groups[pid] = groups[pid] || [];
      groups[pid].push(p);
    }
    for (const [pid, members] of Object.entries(groups)) {
      for (const m of members) size[m.id] = { size: members.length, party_id: pid, members };
    }
    return size;
  })();
  const PARTY_COLOURS = [
    "border-[#FFB020] text-[#FFB020]",
    "border-[#FF4500] text-[#FF4500]",
    "border-[#B8A5FF] text-[#B8A5FF]",
    "border-[#FF8FCB] text-[#FF8FCB]",
    "border-[#00E5B0] text-[#00E5B0]",
  ];
  const partyPalette = (() => {
    const map = {};
    let i = 0;
    for (const p of allPlayers) {
      const pid = p.party_id;
      if (!pid || map[pid] !== undefined) continue;
      map[pid] = PARTY_COLOURS[i % PARTY_COLOURS.length];
      i += 1;
    }
    return map;
  })();
  const PartyChip = ({ id: pid }) => {
    const info = partySizeById[pid];
    if (!info) return null;
    const names = info.members.map((m) => `${m.first_name} ${m.last_name}`).join(", ");
    return (
      <span
        className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${partyPalette[info.party_id] || "border-[#FFB020] text-[#FFB020]"}`}
        data-testid={`party-chip-${info.party_id}`}
        title={`Party of ${info.size} · ${names}`}
      >
        Party · {info.size} of 4
      </span>
    );
  };

  // "Active for this festival" = anything not inactive. Toggling flips inactive↔arrived.
  const setActive = (p, active) =>
    call(() => api.patch(`/festival/${id}/players/${p.id}`, { status: active ? "arrived" : "inactive" }));
  const setStatus = async (p, status) => {
    if (blockWrite()) return;
    // Iter353 · Bug 1 — Explicit confirmation before releasing a
    // coaching-locked player back into the pool. Prevents an
    // accidental tick from silently vacating their coaching slot.
    if (p.status === "coaching_locked" && status !== "coaching_locked") {
      const ok = window.confirm(
        `${p.first_name} ${p.last_name} is locked to a coaching session starting soon. ` +
        `Releasing them will free their coaching slot. Continue?`,
      );
      if (!ok) return;
    }
    if (busy.current) return; busy.current = true;
    try {
      const r = await api.patch(`/festival/${id}/players/${p.id}`, { status });
      // Iter198 — toast if the check-in auto-filled a court.
      if (r.data && r.data._auto_assigned_court) {
        toast.success(`${r.data._auto_assigned_court.name} auto-filled after check-in`);
      } else if (STATUS_LABELS[status]) {
        toast.success(STATUS_LABELS[status]);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { busy.current = false; }
  };
  // Iter462 — Direct-api handlers (status change, place, release, swap,
  // play-request response) that bypass `call()` are gated here with
  // the same `blockWrite()` toast so no mutation path can slip past
  // the post-festival read-only lock.
  const toggleRatingMode = () =>
    call(() => api.patch(`/festival/${id}/ops`, { rating_display: ratingMode === "band" ? "dupr" : "band" }));
  const toggleAssignMode = () =>
    call(() => api.patch(`/festival/${id}/ops`, { assignment_mode: config.assignment_mode === "balanced" ? "all_in" : "balanced" }));

  const openMenu = (e, p) => {
    e.preventDefault?.();
    const x = e.clientX ?? (e.touches?.[0]?.clientX) ?? window.innerWidth / 2;
    const y = e.clientY ?? (e.touches?.[0]?.clientY) ?? window.innerHeight / 2;
    // Iter192 — clamp so long-press near screen edges keeps the menu visible.
    const clampedX = Math.min(x, window.innerWidth - 220);
    const clampedY = Math.min(y, window.innerHeight - 320);
    setMenu({ player: p, x: clampedX, y: clampedY });
    setMenuSub({ coaching: false, moveTo: false });
  };
  // Iter192 — long-press for touch devices. 500ms hold ⇒ open menu.
  const startLongPress = (e, p) => {
    longPressRef.current.moved = false;
    if (longPressRef.current.timer) clearTimeout(longPressRef.current.timer);
    longPressRef.current.timer = setTimeout(() => {
      if (!longPressRef.current.moved) {
        try { if (navigator.vibrate) navigator.vibrate(20); } catch {}
        openMenu(e, p);
      }
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressRef.current.timer) {
      clearTimeout(longPressRef.current.timer);
      longPressRef.current.timer = null;
    }
  };
  const noteMove = () => { longPressRef.current.moved = true; };
  // Iter316 — Resolutions for the locked-group drag confirm modal.
  const resolveLockedDrag = async (action) => {
    const m = lockedDragModal;
    if (!m) return;
    setLockedDragModal(null);
    if (action === "cancel") return;  // snap back, no change
    try {
      if (action === "detach") {
        // Permanent detach — remove this player from the group. If the
        // group would drop below 2 members the backend dissolves it.
        await api.post(
          `/festival/${id}/play-requests/${m.request.id}/detach-member`,
          { player_id: m.playerId },
        );
      }
      // Both "keep" and "detach" proceed with the actual placement.
      await api.post(`/festival/${id}/courts/${m.courtId}/place`, {
        player_id: m.playerId, slot: m.slot,
      });
      toast.success(action === "detach" ? "Detached & moved" : "Moved · group waiting");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    }
  };

  const menuPlace = async (court) => {
    if (!menu?.player) return;
    // Iter327 · Bug 3 — Right-click place must go through the same
    // full-court swap prompt as drag-drop. Previously the right-click
    // path called /place directly and silently overwrote whichever
    // player Mongo returned last in `current_player_ids`.
    if (openSwapPromptIfFull(court, "current", menu.player.id)) {
      setMenu(null);
      return;
    }
    await call(() => api.post(`/festival/${id}/courts/${court.id}/place`, { player_id: menu.player.id, slot: "current" }), `→ ${court.name}`);
    setMenu(null);
  };

  const onDragStart = (e, pid) => {
    setDragPid(pid);
    dragActiveRef.current = true;
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };

  // Iter327 · Bug 1 — Global cleanup so the poll can resume and the
  // freeze state doesn't linger after a cancelled drag (Esc, drop
  // outside a valid target, or browser tab switch).
  const onDragEnd = () => {
    dragActiveRef.current = false;
    setDragPid(null);
  };

  // Iter316 — Find any locked/live play_request that contains the
  // dragged player. Rejecters are excluded (they don't count as
  // members). Returns null if the player isn't in a locked group.
  const findLockedGroupForPlayer = (pid) => {
    if (!pid) return null;
    for (const r of playRequests || []) {
      if (!r || !["locked", "live"].includes(r.status)) continue;
      const memberIds = (r.members || [])
        .filter((m) => m.response !== "rejected")
        .map((m) => m.player_id);
      if (memberIds.includes(pid)) return r;
    }
    return null;
  };
  // Iter285 — Full-court detection helper. Returns true if the drop
  // was handled (either the swap prompt was cancelled or the modal
  // opened) so the caller should NOT proceed with the /place call.
  const openSwapPromptIfFull = (target, slot, incomingPid) => {
    if (!target || !incomingPid) return false;
    // Iter341 · New bug — Drag-and-drop must respect the per-session
    // coaching max (default 6) on ANY reserved_coaching court, not
    // just when the court currently has zero players. The legacy
    // hard-cap of 4 was applied once a single player was seated,
    // preventing organisers from dragging the remaining coaching
    // players onto a 6-player session.
    const isCoachingCourt = target.status === "reserved_coaching" || !!target.coaching_slot_at;
    const isCoachingSignups = isCoachingCourt
      && (target.current_player_ids || []).length === 0
      && slot === "current";
    const rosterField = isCoachingSignups ? "coaching_signup_ids" : (slot === "next_up" ? "next_up_player_ids" : "current_player_ids");
    const roster = target[rosterField] || [];
    const cap = isCoachingCourt
      ? (Number(target.coaching_max_players) || 6)
      : 4;
    if (roster.length < cap) return false;
    if (roster.includes(incomingPid)) return false;
    const ok = window.confirm("This court is already full. Do you want to swap a player out to make room?");
    if (!ok) return true;
    setSwapModal({
      courtId: target.id,
      slot: isCoachingSignups ? "coaching_signups" : slot,
      incomingPid,
      existingPids: [...roster],
      label: target.name || "Court",
    });
    return true;
  };

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  // Iter348 · Bug 2 — Drag a player from any court back to the
  // Registration/Available panel. Removes them from every court
  // roster (current, next-up, coaching signups) and flips their
  // status to available so they re-appear in the Signed-Up list.
  const onDropToPool = async (e) => {
    e.preventDefault();
    if (!dragPid) return;
    const pid = dragPid;
    const p = byId[pid];
    const name = p ? `${p.first_name} ${p.last_name}`.trim() : "Player";
    setDragPid(null);
    dragActiveRef.current = false;
    try {
      await api.post(`/festival/${id}/players/${pid}/release-to-pool`);
      toast.success(`${name} → Available pool`);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message) || "Couldn't release player");
    }
  };
  const onDrop = (e, courtId, slot) => {
    e.preventDefault();
    if (!dragPid) return;
    // Iter316 — Locked-group drag confirm. If the dragged player is a
    // member of a locked/live play_request AND the target court isn't
    // the group's current court, warn the desk before we break the
    // group. The modal offers three resolutions: keep grouping (just
    // move — waiting logic keeps the rest of the group intact), detach
    // the player permanently, or cancel the drop entirely.
    const lockedGroup = findLockedGroupForPlayer(dragPid);
    if (lockedGroup) {
      const groupCourtId = lockedGroup.court_id;
      if (!groupCourtId || groupCourtId !== courtId) {
        setLockedDragModal({ courtId, slot, playerId: dragPid, request: lockedGroup });
        setDragPid(null);
        return;
      }
    }
    // Iter266 — Party-aware drop. If the dragged player is part of a
    // linked team (accepted mutual partner request → shared party_id),
    // ask whether to bring the whole team along. On Yes we hit the
    // new /place-party endpoint which yanks every available team-mate
    // onto this court AND tops up unlinked singles. On No we fall
    // through to the legacy single-player /place.
    const dragged = byId[dragPid];
    const partyInfo = dragged ? partySizeById[dragPid] : null;
    if (partyInfo && partyInfo.size > 1 && slot === "current") {
      const others = (partyInfo.members || [])
        .filter((m) => m.id !== dragPid)
        .map((m) => `${m.first_name} ${m.last_name}`)
        .join(", ");
      const ok = window.confirm(
        `${dragged.first_name} ${dragged.last_name} is part of a team of ${partyInfo.size}` +
        (others ? ` (with ${others}).` : ".") +
        `\n\nAdd all available team members to this court?\n\n` +
        `OK = seat the whole team here (partial court will be topped up with unlinked players).\n` +
        `Cancel = seat just this player.`
      );
      if (ok) {
        call(() => api.post(`/festival/${id}/courts/${courtId}/place-party`, { player_id: dragPid, slot }), "Team placed");
        setDragPid(null);
        return;
      }
    }
    // Iter281 — Block drag if target is an active coaching court
    // and the dragged player is already registered on it.
    const target = (state?.courts || []).find((c) => c.id === courtId);
    if (target && target.status === "reserved_coaching") {
      const isRegistered = (target.coaching_signup_ids || []).includes(dragPid)
        || (target.current_player_ids || []).includes(dragPid);
      if (isRegistered) {
        toast.error("This player is already registered for the coaching session on this court.");
        setDragPid(null);
        return;
      }
      let remainingMin = null;
      if (target.coaching_slot_at) {
        try {
          const start = new Date(target.coaching_slot_at).getTime();
          const dur = (target.coaching_duration_minutes || 60) * 60000;
          const end = start + dur;
          const now = Date.now();
          if (now >= start && end > now) {
            remainingMin = Math.max(1, Math.round((end - now) / 60000));
          }
        } catch {}
      }
      if (remainingMin != null) {
        const ok = window.confirm(
          `This court has an active coaching session with ${remainingMin} minute${remainingMin === 1 ? "" : "s"} remaining. Are you sure you want to assign this player here?`,
        );
        if (!ok) { setDragPid(null); return; }
      }
    }
    // Iter285 — Detect full-court drop and open the swap prompt.
    if (openSwapPromptIfFull(target, slot, dragPid)) {
      setDragPid(null);
      return;
    }
    call(() => api.post(`/festival/${id}/courts/${courtId}/place`, { player_id: dragPid, slot }), "Moved");
    setDragPid(null);
    dragActiveRef.current = false;
  };

  const activeCount = allPlayers.filter((p) => p.status !== "inactive").length;

  // Iter195 — Live court count control for the header input.
  const courtCount = state?.courts?.length || 0;
  const setCourtCount = async (n) => {
    const clamped = Math.max(2, Math.min(20, Number(n) || 0));
    if (clamped === courtCount) return;
    await call(() => api.post(`/festival/${id}/courts/reset`, { count: clamped }), `${clamped} courts`);
  };
  // Iter204 — Live pre-start seconds control (0 = disabled).
  const setPreStartSeconds = async (n) => {
    const raw = Number(n);
    if (Number.isNaN(raw)) return;
    const clamped = Math.max(0, Math.min(1800, Math.round(raw)));
    if (clamped === (config.pre_start_seconds ?? 300)) return;
    await call(() => api.patch(`/festival/${id}/ops`, { pre_start_seconds: clamped }),
      clamped === 0 ? "Pre-start disabled" : `Pre-start ${clamped}s`);
  };
  // Iter210 — Global default game duration (minutes).
  const setGameMinutes = async (n) => {
    const raw = Number(n);
    if (Number.isNaN(raw)) return;
    const clamped = Math.max(1, Math.min(90, Math.round(raw)));
    if (clamped === (config.court_default_minutes ?? 12)) return;
    await call(() => api.patch(`/festival/${id}/ops`, { court_default_minutes: clamped }),
      `Game default ${clamped}min`);
  };
  // Iter210 — Per-court game duration override.
  const setCourtMinutes = async (courtId, n) => {
    const raw = Number(n);
    if (Number.isNaN(raw)) return;
    const clamped = Math.max(1, Math.min(90, Math.round(raw)));
    await call(() => api.patch(`/festival/${id}/courts/${courtId}`, { duration_min: clamped }),
      `Court game ${clamped}min`);
  };

  // Iter195 — Friendly status hint for hover tooltip on each player row.
  const statusHint = (p) => {
    if (!state) return STATUS_LABELS[p.status] || p.status;
    for (const c of state.courts || []) {
      if ((c.current_player_ids || []).includes(p.id)) return `On ${c.name}${c.state === "live" ? " · playing" : c.state === "ready" ? " · pre-start" : ""}`;
      if ((c.next_up_player_ids || []).includes(p.id)) return `Next Up on ${c.name}`;
    }
    if (p.status === "resting") return "Resting" + (p.rest_reason ? ` · ${p.rest_reason}` : "");
    if (p.status === "sin_bin") return "Sin-bin" + (p.sin_bin_reason ? ` · ${p.sin_bin_reason}` : "");
    if (p.status === "arrived") return "Arrived · needs to mark Available";
    if (p.status === "available") return "Available · ready to play";
    if (p.status === "imported") return "Imported · not yet checked in";
    if (p.status === "inactive") return "Inactive";
    return STATUS_LABELS[p.status] || p.status;
  };

  // Iter196 — One-tap check-in / activate. On first tap of an imported
  // player, immediately mark arrived (no popup — keeps the flow snappy).
  // Menu-driven Check-in handles the "ready to play?" confirm popup.
  const quickAdvance = async (p) => {
    if (p.status === "imported") return setStatus(p, "arrived");
    if (p.status === "arrived" || p.status === "resting" || p.status === "sin_bin" || p.status === "inactive") return setStatus(p, "available");
    if (p.status === "available") return setStatus(p, "resting");
  };


  return (
    <div className="w-full mx-auto px-3 sm:px-4 pt-4 lg:pt-16 pb-4" data-testid="festival-live-ops">
      {/* Iter462 — Post-festival read-only banner. Rendered at the
          very top so it's the first thing a non-admin sees once the
          festival's `end_at` has passed. Identical treatment to
          Player Manager (Iter461). */}
      {isReadOnly && <FestivalEndedBanner />}
      {/* Iter326 · Bug 1 — Early-access soft lock. Rendered ABOVE the
          normal ops chrome so helpers still see the festival header and
          can navigate back to Players (check-in prep is still allowed
          before Live Ops opens). */}
      {liveLocked && (
        <div
          className="mb-4 border border-[#FFB020] bg-[#1A140A] p-4 flex items-start gap-3"
          data-testid="ops-live-locked-banner"
        >
          <ShieldCheck className="w-5 h-5 text-[#FFB020] shrink-0 mt-0.5" />
          <div className="text-sm text-white">
            <div className="label-mono text-[#FFB020] text-[10px] mb-1">LIVE OPS OPENS SOON</div>
            <div>{liveLocked.message || "Live Ops isn't open yet for this festival."}</div>
            {liveLocked.live_ops_locked_until && (
              <div className="text-xs text-[#A3A3A3] mt-1" data-testid="ops-live-locked-until">
                Unlocks {new Date(liveLocked.live_ops_locked_until).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Iter321 — Over-cap soft block + upgrade modal. */}
      {OverCapBanner}
      {UpgradeModal}
      {/* Iter235b — Quick helper pass modal (owner + festival_only_helper). */}
      <HelperPassQuickModal
        festivalId={id}
        open={showHelperQuick}
        onClose={() => setShowHelperQuick(false)}
        licenseActive={["active_paid", "complimentary", "override"].includes(festival?.license?.state)}
      />
      {/* Iter300 — Sponsor Ribbon manager (owner-only). */}
      <SponsorRibbonModal
        festivalId={id}
        open={showSponsorsModal}
        onClose={() => setShowSponsorsModal(false)}
      />
      {/* Iter315 — Skill Ranges editor (owner-only, opens from header). */}
      <SkillRangesModal
        festivalId={id}
        open={skillRangesOpen}
        onClose={() => setSkillRangesOpen(false)}
        onSaved={() => { load(); }}
      />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        {/* Iter333 · Bug 18 — Helper sessions have no Rally-Lab
            account, so the "back to festival home" link at the top
            of Live Ops used to bounce them off ProtectedRoute →
            /login. Owners + full-editor roles still get the crumb;
            helpers see nothing (matches the Check-In view's
            pattern from Iter326 Bug 3). */}
        {!isHelper ? (
          <button type="button" onClick={() => navigate(`/festival/${id}`)} data-testid="ops-back-crumb" className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> {festival.name}
          </button>
        ) : <span className="text-[11px] uppercase tracking-wider text-[#A3A3A3]" data-testid="ops-back-crumb-helper">{festival.name}</span>}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3]" data-testid="ops-active-count">
            Active: {activeCount} / {allPlayers.length}
          </span>
          <button type="button" onClick={toggleRatingMode} data-testid="ops-rating-toggle" className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00]">
            Ratings: {ratingMode === "band" ? "Colour Band" : "DUPR"}
          </button>
          <button type="button" onClick={() => setSkillRangesOpen(true)} disabled={isReadOnly} data-testid="ops-skill-ranges" title={isReadOnly ? "Festival ended — read-only" : "Edit skill ranges"} className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB020]">
            Skill Ranges
          </button>
          <button type="button" onClick={toggleAssignMode} data-testid="ops-mode-toggle" className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF]">
            Mode: {config.assignment_mode === "balanced" ? "Balanced" : "All-In"}
          </button>
          <button type="button" onClick={() => setShowAdd(true)} disabled={isReadOnly} data-testid="ops-add-player" title={isReadOnly ? "Festival ended — read-only" : undefined} className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#CCFF00]">
            <UserPlus className="w-3 h-3" /> Add Player
          </button>
          {canGenerateHelpers && (
            <button
              type="button"
              onClick={() => setShowHelperQuick(true)}
              disabled={isReadOnly}
              data-testid="ops-helper-quick-cta"
              className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#66C2FF]"
              title={isReadOnly ? "Festival ended — read-only" : "Generate a QR + code pass to onboard a helper on the fly"}
            >
              <ShieldCheck className="w-3 h-3" /> Add Helper
            </button>
          )}
          <Link to={`/festival/${id}/players`} data-testid="ops-import-link" className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border border-[#FF2BD6] bg-[#FF2BD6] text-[#0A0A0A] shadow-[0_0_14px_rgba(255,43,214,0.6)] hover:bg-[#FF66E3] hover:border-[#FF66E3] transition-colors inline-flex flex-col items-center leading-tight" title="Open Player Manager to import or edit the roster">
            <span className="inline-flex items-center gap-1"><Upload className="w-3 h-3" /> Player Manager</span>
            <span className="text-[8px] text-[#0A0A0A]/70">Import / Edit</span>
          </Link>
          {/* Iter305 — External View button + QR dropdown. The
              Link keeps its original behaviour (opens the viewer in a
              new tab). The chevron on the right opens a small popover
              anchored beneath the button showing a scannable QR to
              the same URL — big enough (208px) to scan from ~30 cm
              away without zooming. */}
          <div className="relative inline-flex" data-testid="ops-viewer-group">
            <Link
              to={`/festival/${id}/viewer`}
              target="_blank"
              data-testid="ops-open-viewer"
              className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-r-0 border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"
            >
              Public viewer ↗
            </Link>
            <button
              type="button"
              onClick={() => setShowViewerQr((v) => !v)}
              data-testid="ops-viewer-qr-toggle"
              aria-label="Show QR code for public viewer"
              title="Scan on a phone to open the public viewer"
              className={`px-2 py-1.5 text-[10px] border border-[#66C2FF] inline-flex items-center ${showViewerQr ? "bg-[#66C2FF] text-black" : "text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"}`}
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showViewerQr ? "rotate-180" : ""}`} />
            </button>
            {showViewerQr && (
              <>
                {/* click-outside veil (transparent) */}
                <button
                  type="button"
                  aria-label="Close QR"
                  onClick={() => setShowViewerQr(false)}
                  className="fixed inset-0 z-[70] bg-transparent cursor-default"
                  tabIndex={-1}
                />
                <div
                  className="absolute right-0 top-full mt-1 z-[80] bg-[#0A0A0A] border border-[#66C2FF] p-4 shadow-2xl"
                  data-testid="ops-viewer-qr-popover"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#66C2FF] mb-2 flex items-center gap-1">
                    <QrCode className="w-3 h-3" /> Public viewer
                  </div>
                  <div className="bg-white p-2 rounded" data-testid="ops-viewer-qr-svg">
                    <QRCodeSVG
                      value={`${window.location.origin}/festival/${id}/viewer`}
                      size={416}
                      level="H"
                      includeMargin={false}
                      shapeRendering="crispEdges"
                      style={{ width: 208, height: 208, imageRendering: "pixelated" }}
                    />
                  </div>
                  <div className="mt-2 text-[10px] text-[#A3A3A3] max-w-[220px] break-all leading-snug" title="Public viewer URL">
                    {`${window.location.origin}/festival/${id}/viewer`}
                  </div>
                </div>
              </>
            )}
          </div>
          {!isHelper && (
            <button
              type="button"
              onClick={() => setShowSponsorsModal(true)}
              disabled={isReadOnly}
              data-testid="ops-sponsors-btn"
              className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB020]"
              title={isReadOnly ? "Festival ended — read-only" : "Manage the rotating sponsor ribbon on the Public Viewer"}
            >
              <Megaphone className="w-3 h-3" /> Sponsors
            </button>
          )}
          {/* Iter305 — Alerts (success-toast) toggle. Off suppresses the
              green confirmation toasts stacking in the top-right during
              busy OPS work. Errors & warnings are never silenced. */}
          <button
            type="button"
            onClick={toggleAlerts}
            data-testid="ops-alerts-toggle"
            aria-pressed={!alertsOff}
            title={alertsOff ? "Success toasts are hidden — errors still show. Click to re-enable." : "Click to silence success/confirmation toasts (errors still show)."}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider border inline-flex items-center gap-1 ${alertsOff ? "border-[#525252] text-[#A3A3A3] hover:border-white" : "border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"}`}
          >
            {alertsOff ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />} Alerts: {alertsOff ? "OFF" : "ON"}
          </button>
          {/* Iter339 · Bug 13 — Persistent notifications hub with unread badge. */}
          <NotificationsPanel />
          {/* Iter295/296 — OPS ROWS: courts-per-row for THIS OPS view
              only. Independent of the SCREEN ROWS viewer setting. */}
          <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="How many court cards per row in the OPS view (Auto = responsive, 1-6 = fixed). Independent of the Public Viewer.">
            OPS ROWS:
            <select
              value={opsCols || 0}
              onChange={(e) => setOpsColsPersist(e.target.value)}
              data-testid="ops-cols-per-row"
              className="bg-transparent text-white px-1 py-0.5 focus:outline-none"
            >
              <option value={0} className="bg-black">Auto</option>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n} className="bg-black">{n}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={toggleFullscreen}
            data-testid="ops-fullscreen-toggle"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00]"
            title="Toggle full-screen mode"
          >
            Full-screen ⤢
          </button>
          {/* Iter291 — Hide/Show Chrome button removed. Sidebar auto-
              hides in Festival OPS via Layout.jsx iter288. */}
          {isHelper ? null : (<>
          {/* Iter195 — Live court-count control. */}
          <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="Set the number of active courts (2-20). Applies live.">
            Courts:
            <input
              type="number"
              min={2}
              max={20}
              defaultValue={courtCount}
              key={`court-count-${courtCount}`}
              onBlur={(e) => setCourtCount(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              data-testid="ops-court-count"
              className="w-12 bg-transparent border-b border-[#525252] text-center focus:outline-none focus:border-[#CCFF00]"
            />
          </label>
          {/* Iter204 — Pre-start countdown length control. 0 disables. */}
          <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="Length of the pre-start countdown before each game (seconds). 0 disables — games start the moment a court fills.">
            Pre-start:
            <input
              type="number"
              min={0}
              max={1800}
              step={30}
              defaultValue={config.pre_start_seconds ?? 300}
              key={`prestart-${config.pre_start_seconds ?? 300}`}
              onBlur={(e) => setPreStartSeconds(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              data-testid="ops-prestart-seconds"
              className={`w-14 bg-transparent border-b text-center focus:outline-none focus:border-[#CCFF00] ${config.pre_start_seconds === 0 ? "border-[#FF4500] text-[#FF4500]" : "border-[#525252]"}`}
            />
            <span className={`text-[9px] ml-0.5 ${config.pre_start_seconds === 0 ? "text-[#FF4500]" : "text-[#525252]"}`} data-testid="ops-prestart-label">
              {config.pre_start_seconds === 0 ? "OFF" : "s"}
            </span>
          </label>
          {/* Iter210 — Global default game duration (minutes). Overridable per-court. */}
          <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="Default game length in minutes. Individual courts can override this on the court card.">
            Game:
            <input
              type="number"
              min={1}
              max={90}
              defaultValue={config.court_default_minutes ?? 12}
              key={`game-min-${config.court_default_minutes ?? 12}`}
              onBlur={(e) => setGameMinutes(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              data-testid="ops-game-minutes"
              className="w-12 bg-transparent border-b border-[#525252] text-center focus:outline-none focus:border-[#CCFF00]"
            />
            <span className="text-[9px] text-[#525252] ml-0.5">min</span>
          </label>
          {/* Iter213 — Gender-aware play mode selector. */}
          <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="How auto-fill treats gender. Mixed = anyone; Same-gender = all-M or all-F quads; Mixed-doubles = 2M + 2F per court. Ignored for players with no gender set.">
            Play:
            <select
              value={config.play_mode || "mixed"}
              onChange={(e) => call(() => api.patch(`/festival/${id}/ops`, { play_mode: e.target.value }), `Play mode: ${e.target.value.replace('_', ' ')}`)}
              data-testid="ops-play-mode"
              className="bg-transparent border-b border-[#525252] text-center focus:outline-none focus:border-[#CCFF00] pr-1"
            >
              <option value="mixed">Mixed</option>
              <option value="same_gender">Same-gender</option>
              <option value="mixed_doubles">Mixed doubles</option>
            </select>
          </label>
          {/* Iter393 — Gender match wait window (minutes). Only visible
               when play_mode enforces gender balance. Hidden on
               `mixed` (no preference) per spec. */}
          {(config.play_mode === "same_gender" || config.play_mode === "mixed_doubles") && (
            <label
              className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]"
              title="Minutes to wait for a gender-balanced group before falling back to a mixed court. Range 1-10, default 3."
            >
              Wait:
              <input
                type="number"
                min={1}
                max={10}
                value={config.gender_match_wait_minutes ?? 3}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(10, parseInt(e.target.value || "3", 10) || 3));
                  call(
                    () => api.patch(`/festival/${id}/ops`, { gender_match_wait_minutes: v }),
                    `Gender wait: ${v} min`,
                  );
                }}
                data-testid="ops-gender-wait"
                className="w-10 bg-transparent border-b border-[#525252] text-center focus:outline-none focus:border-[#CCFF00]"
              />
              <span className="text-[9px] text-[#525252] ml-0.5">min</span>
            </label>
          )}
          {/* Iter205 — Buzzer chime toggle. */}
          <button
            type="button"
            onClick={toggleChime}
            data-testid="ops-chime-toggle"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider border inline-flex items-center gap-1 ${chimeEnabled ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#525252]"}`}
            title={chimeEnabled ? "Chime plays when the pre-start countdown ends" : "Chime muted"}
          >
            Chime: {chimeEnabled ? "ON" : "OFF"}
          </button>
          {/* Iter259 — Viewer scroll: dropdown picks a BAND (off/slow/
              medium/fast) with visibly different speed ranges; slider
              fine-tunes within the active band only. Off disables the
              slider entirely; every other band's leftmost slider still
              scrolls (never stops unless dropdown is Off). */}
          {(() => {
            const BANDS = {
              off:    { min: 0,   max: 0,   mid: 0 },
              slow:   { min: 15,  max: 40,  mid: 27 },
              medium: { min: 55,  max: 100, mid: 77 },
              fast:   { min: 120, max: 200, mid: 160 },
            };
            const band = config.viewer_scroll_speed || "medium";
            const b = BANDS[band] || BANDS.medium;
            const curPx = typeof config.viewer_scroll_px_s === "number" ? config.viewer_scroll_px_s : b.mid;
            const isOff = band === "off";
            return (
              <>
                <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="Speed band for the Public Viewer auto-scroll. The slider on the right fine-tunes within the chosen band.">
                  Viewer scroll:
                  <select
                    value={band}
                    onChange={(e) => {
                      const nb = e.target.value;
                      const mid = BANDS[nb]?.mid ?? 0;
                      call(() => api.patch(`/festival/${id}/ops`, {
                        viewer_scroll_speed: nb,
                        viewer_scroll_px_s: mid,
                      }), `Viewer scroll: ${nb}`);
                    }}
                    data-testid="ops-viewer-scroll-speed"
                    className="bg-transparent border-b border-[#525252] text-center focus:outline-none focus:border-[#CCFF00] pr-1"
                  >
                    <option value="off">Off</option>
                    <option value="slow">Slow</option>
                    <option value="medium">Medium</option>
                    <option value="fast">Fast</option>
                  </select>
                </label>
                <label className={`flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-wider border ${isOff ? "border-[#262626] opacity-40" : "border-[#262626]"}`} title={isOff ? "Off — slider disabled" : `Fine-tune within the ${band.toUpperCase()} band (${b.min}-${b.max} px/s)`}>
                  <span>Speed slider:</span>
                  <input
                    type="range"
                    min={b.min}
                    max={b.max || 1}
                    step={1}
                    disabled={isOff}
                    defaultValue={isOff ? 0 : Math.max(b.min, Math.min(b.max, curPx))}
                    key={`spx-${band}-${curPx}`}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10) || 0;
                      const lbl = e.target.parentElement.querySelector('[data-testid="ops-viewer-scroll-px-s-label"]');
                      if (lbl) lbl.textContent = `${v} px/s`;
                    }}
                    onMouseUp={(e) => call(() => api.patch(`/festival/${id}/ops`, {
                      viewer_scroll_px_s: Math.max(b.min, Math.min(b.max, parseInt(e.target.value, 10) || b.min)),
                    }), "Viewer speed updated")}
                    onTouchEnd={(e) => call(() => api.patch(`/festival/${id}/ops`, {
                      viewer_scroll_px_s: Math.max(b.min, Math.min(b.max, parseInt(e.target.value, 10) || b.min)),
                    }), "Viewer speed updated")}
                    data-testid="ops-viewer-scroll-px-s"
                    data-band={band}
                    data-band-min={b.min}
                    data-band-max={b.max}
                    className={`w-24 accent-[#CCFF00] ${isOff ? "cursor-not-allowed" : ""}`}
                  />
                  <span data-testid="ops-viewer-scroll-px-s-label" className="text-[9px] text-[#525252] w-14 text-right">
                    {isOff ? "OFF" : `${curPx} px/s`}
                  </span>
                </label>
              </>
            );
          })()}
          {/* Iter252/296 — SCREEN ROWS: courts-per-row on the Public
              Viewer (Auto or 1-6). Was numeric 0-10 with a min-4 snap;
              now a clean select capped at 6. */}
          <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="How many court cards per row on the Public Viewer (Auto = responsive, 1-6 = fixed).">
            SCREEN ROWS:
            <select
              value={(config.viewer_columns ?? 0)}
              onChange={(e) => {
                const v = Math.max(0, Math.min(6, parseInt(e.target.value, 10) || 0));
                call(() => api.patch(`/festival/${id}/ops`, { viewer_columns: v || 0 }), v === 0 ? "Viewer: auto" : `Viewer: ${v} per row`);
              }}
              data-testid="ops-viewer-columns"
              className="bg-transparent text-white px-1 py-0.5 focus:outline-none"
            >
              <option value={0} className="bg-black">Auto</option>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n} className="bg-black">{n}</option>
              ))}
            </select>
          </label>
          {/* Iter216 — Partner request auto-decline expiry (seconds). 0 = disabled. */}
          <label className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="Auto-decline pending partner requests older than this many seconds (default 600 = 10 min). 0 disables auto-decline.">
            Partner expiry:
            <input
              type="number"
              min={0}
              max={3600}
              step={60}
              defaultValue={config.partner_request_expiry_seconds ?? 600}
              key={`pexp-${config.partner_request_expiry_seconds ?? 600}`}
              onBlur={(e) => call(() => api.patch(`/festival/${id}/ops`, { partner_request_expiry_seconds: Math.max(0, Math.min(3600, Math.round(Number(e.target.value) || 0))) }), "Partner expiry updated")}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              data-testid="ops-partner-expiry"
              className={`w-14 bg-transparent border-b text-center focus:outline-none focus:border-[#CCFF00] ${(config.partner_request_expiry_seconds ?? 600) === 0 ? "border-[#FF4500] text-[#FF4500]" : "border-[#525252]"}`}
            />
            <span className={`text-[9px] ml-0.5 ${(config.partner_request_expiry_seconds ?? 600) === 0 ? "text-[#FF4500]" : "text-[#525252]"}`}>
              {(config.partner_request_expiry_seconds ?? 600) === 0 ? "OFF" : "s"}
            </span>
          </label>
          {/* Iter268/271 — Queue multi-games ON/OFF + admin MAX (1-4).
              Recommended OFF near end of day. Lowering max caps every
              player's queue_remaining immediately. */}
          <label className="flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#262626]" title="When ON, players choose 1-N consecutive games; N is the admin-set MAX (1-4). Recommended OFF near end of day.">
            Queue multi-games:
            <button
              type="button"
              data-testid="ops-queue-multi-games"
              onClick={() => call(
                () => api.patch(`/festival/${id}/ops`, { queue_multi_games_enabled: !config.queue_multi_games_enabled }),
                `Multi-game queue ${config.queue_multi_games_enabled ? "disabled" : "enabled"}`,
              )}
              className={`px-2 py-0.5 border ${config.queue_multi_games_enabled ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#525252] text-[#525252]"}`}
            >
              {config.queue_multi_games_enabled ? "ON" : "OFF"}
            </button>
            <span className="text-[9px] text-[#525252]">max</span>
            <div className="flex gap-0.5" data-testid="ops-queue-multi-games-max-picker">
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`ops-queue-multi-games-max-${n}`}
                  onClick={() => call(
                    () => api.patch(`/festival/${id}/ops`, { queue_multi_games_max: n }),
                    `Max queue set to ${n}`,
                  )}
                  className={`w-5 h-5 text-[10px] font-bold border ${(config.queue_multi_games_max || 1) === n ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent border-[#525252] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00]"}`}
                >{n}</button>
              ))}
            </div>
          </label>
          {/* Iter191 — Pending Requests badge/tray. */}
          <button
            type="button"
            onClick={() => setShowTray(true)}
            data-testid="ops-pending-badge"
            className={`relative px-3 py-1.5 text-[10px] uppercase tracking-wider border inline-flex items-center gap-1 hover:border-white hover:text-white ${pending.length > 0 ? "border-[#FFB020] text-[#FFB020]" : "border-[#66C2FF] text-[#66C2FF]"}`}
            title="Partner requests waiting for accept/decline"
          >
            <Bell className="w-3 h-3" /> Requests
            {pending.length > 0 && (
              <span data-testid="ops-pending-count" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#FFB020] text-black text-[9px] font-bold rounded-full flex items-center justify-center">
                {pending.length}
              </span>
            )}
          </button>
          </>)}
        </div>
      </div>

      {/* Iter274/280 — Coaching Setup Modal. */}
      {coachingModalCourt && (
        <CoachingSetupModal
          court={coachingModalCourt}
          allCourts={courts}
          api={api}
          festivalId={id}
          onClose={() => setCoachingModalCourt(null)}
          onSaved={() => { setCoachingModalCourt(null); load(); }}
        />
      )}

      {/* Iter285 — Swap-Player modal. Renders when admin confirmed the
          swap prompt on a full-court drop. Admin picks who to remove
          and the backend swap endpoint replaces them with the dragged
          player in-place. */}
      {swapModal && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={() => setSwapModal(null)} data-testid="swap-player-modal">
          <div className="w-full max-w-sm bg-[#0A0A0A] border border-[#CCFF00] p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="text-[#CCFF00] uppercase tracking-widest font-bold text-sm">Swap out · {swapModal.label}</div>
              <button type="button" onClick={() => setSwapModal(null)} data-testid="swap-modal-close" className="text-[#A3A3A3] hover:text-white text-lg leading-none">×</button>
            </div>
            <div className="text-[11px] text-[#A3A3A3]">
              Choose a player to remove. They return to the available pool and {byId[swapModal.incomingPid] ? `${byId[swapModal.incomingPid].first_name} ${byId[swapModal.incomingPid].last_name}` : "the dragged player"} takes their place.
            </div>
            <ul className="space-y-1">
              {swapModal.existingPids.map((pid) => {
                const p = byId[pid];
                return (
                  <li key={pid} className="flex items-center justify-between border border-[#262626] px-2 py-1.5 text-white text-sm" data-testid={`swap-player-row-${pid}`}>
                    <span className="flex-1 truncate">{p ? `${p.first_name} ${p.last_name}` : "Player"}</span>
                    <button
                      type="button"
                      data-testid={`swap-remove-${pid}`}
                      onClick={async () => {
                        try {
                          await api.post(`/festival/${id}/courts/${swapModal.courtId}/swap-player`, {
                            remove_player_id: pid,
                            add_player_id: swapModal.incomingPid,
                            slot: swapModal.slot,
                          });
                          toast.success("Swapped");
                          setSwapModal(null);
                          load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Swap failed");
                        }
                      }}
                      className="text-[10px] px-2 py-1 uppercase tracking-widest border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black"
                    >Remove</button>
                  </li>
                );
              })}
            </ul>
            <button type="button" onClick={() => setSwapModal(null)} data-testid="swap-modal-cancel" className="w-full py-2 text-[10px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Iter316 — Locked-Group drag-confirm modal. Fires whenever a
          drag would pull a locked/live play-request member onto a court
          different from the group's own court. Three resolutions in a
          single dialog per user spec. */}
      {lockedDragModal && (() => {
        const req = lockedDragModal.request;
        const draggedName = byId[lockedDragModal.playerId]
          ? `${byId[lockedDragModal.playerId].first_name} ${byId[lockedDragModal.playerId].last_name}`
          : "This player";
        const otherMembers = (req.members || [])
          .filter((m) => m.player_id !== lockedDragModal.playerId && m.response !== "rejected");
        const otherNames = otherMembers
          .map((m) => `${m.first_name} ${m.last_name}`.trim())
          .filter(Boolean)
          .join(", ");
        const willDissolve = otherMembers.length < 2 || lockedDragModal.playerId === req.requester_id;
        const targetCourt = (state?.courts || []).find((c) => c.id === lockedDragModal.courtId);
        return (
          <div
            className="fixed inset-0 z-[85] bg-black/70 flex items-center justify-center p-4"
            onClick={() => resolveLockedDrag("cancel")}
            data-testid="locked-drag-modal"
          >
            <div
              className="w-full max-w-md bg-[#0A0A0A] border border-[#FFB020] p-4 space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div className="text-[#FFB020] uppercase tracking-widest font-bold text-sm">
                  Locked group · confirm move
                </div>
                <button
                  type="button"
                  onClick={() => resolveLockedDrag("cancel")}
                  data-testid="locked-drag-close"
                  className="text-[#A3A3A3] hover:text-white text-lg leading-none"
                >
                  ×
                </button>
              </div>
              <div className="text-[12px] text-[#E5E5E5] leading-snug">
                <span className="text-[#CCFF00] font-bold">{draggedName}</span> is in a locked play group
                {otherNames ? <> with <span className="text-[#66C2FF]">{otherNames}</span></> : null}.
                Moving them to <span className="font-bold">{targetCourt?.name || "this court"}</span> alone will break the group's on-court plan.
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <button
                  type="button"
                  data-testid="locked-drag-keep"
                  onClick={() => resolveLockedDrag("keep")}
                  className="w-full py-2 text-[11px] uppercase tracking-widest border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black text-left px-3"
                >
                  <div className="font-bold">Keep Grouping</div>
                  <div className="normal-case tracking-normal text-[10px] text-[#A3A3A3] mt-0.5">
                    Move now — the group waits for {draggedName.split(" ")[0]} to finish, then all seat together.
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="locked-drag-detach"
                  onClick={() => resolveLockedDrag("detach")}
                  className="w-full py-2 text-[11px] uppercase tracking-widest border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-white text-left px-3"
                >
                  <div className="font-bold">Detach Player</div>
                  <div className="normal-case tracking-normal text-[10px] text-[#A3A3A3] mt-0.5">
                    {willDissolve
                      ? "Removes them permanently — only 1 member would remain, so the group is dissolved."
                      : "Removes them permanently from the group and moves them here."}
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="locked-drag-cancel"
                  onClick={() => resolveLockedDrag("cancel")}
                  className="w-full py-2 text-[11px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white"
                >
                  Cancel · snap back
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Iter191 — Pending Requests tray modal. */}
      {showTray && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4" onClick={() => setShowTray(false)} data-testid="ops-pending-tray">
          <div className="w-full max-w-md bg-[#0A0A0A] border border-[#FFB020] mt-16 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-[#262626]">
              <div className="label-mono text-[#FFB020]">PENDING PARTNER REQUESTS ({pending.length})</div>
              <button type="button" onClick={() => setShowTray(false)} data-testid="ops-pending-close" className="text-[#A3A3A3] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3">
              {pending.length === 0 ? (
                <div className="text-center py-8 text-xs text-[#525252] italic" data-testid="ops-pending-empty">
                  Nothing waiting — every request has been resolved.
                </div>
              ) : (
                <div className="space-y-2">
                  {pending.map((r) => (
                    <div key={`${r.target_id}-${r.from_id}`} data-testid={`ops-pending-row-${r.from_id}-${r.target_id}`} className="border border-[#262626] p-3">
                      <div className="text-sm mb-2">
                        <span className="text-[#CCFF00]">{r.from_name}</span> wants to play with <span className="text-[#66C2FF]">{r.target_name}</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          data-testid={`ops-pending-accept-${r.from_id}-${r.target_id}`}
                          onClick={async () => {
                            try {
                              // Iter330 · Bug 2a — Rows from the new
                              // play-request engine carry
                              // `play_request_id`; route directly to
                              // that endpoint (owns locked-group
                              // recomputation + auto-placement).
                              // Legacy embedded rows still hit the
                              // partner-requests handler.
                              const res = r.play_request_id
                                ? await api.post(`/festival/${id}/play-requests/${r.play_request_id}/respond-as`, {
                                    player_id: r.target_id, action: "approve",
                                  })
                                : await api.post(`/festival/${id}/partner-requests/response`, {
                                    target_id: r.target_id, from_id: r.from_id, action: "accept",
                                  });
                              const placed = res?.data?.placement?.placed;
                              const courtName = res?.data?.placement?.court_name;
                              if (placed && courtName) {
                                toast.success(`${r.from_name} & ${r.target_name} seated on ${courtName}`);
                              } else {
                                toast.success(`Linked ${r.from_name} & ${r.target_name}`);
                              }
                              load();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Couldn't accept");
                            }
                          }}
                          className="flex-1 py-2 text-[10px] uppercase tracking-wider bg-[#CCFF00] text-black font-bold inline-flex items-center justify-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Accept
                        </button>
                        <button
                          type="button"
                          data-testid={`ops-pending-decline-${r.from_id}-${r.target_id}`}
                          onClick={async () => {
                            try {
                              if (r.play_request_id) {
                                // Iter330 · Bug 2a — new-engine path.
                                await api.post(`/festival/${id}/play-requests/${r.play_request_id}/respond-as`, {
                                  player_id: r.target_id, action: "reject",
                                });
                              } else {
                                await api.post(`/festival/${id}/partner-requests/response`, {
                                  target_id: r.target_id, from_id: r.from_id, action: "decline",
                                });
                              }
                              toast.info("Declined");
                              load();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Couldn't decline");
                            }
                          }}
                          className="flex-1 py-2 text-[10px] uppercase tracking-wider border border-[#525252] hover:border-white"
                        >
                          <X className="w-3 h-3 inline mr-1" /> Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Iter245 — Clean fixed 3-column layout. Left aside stacks the
          Registration + Signed-Up + Playing panels (each collapsible with
          a live count in the header); center holds the courts grid;
          right holds Quick Actions (also collapsible). Drag-layout mode
          has been removed — it was unstable and made the courts vanish.
          Iter373 · Fix 4 — Reg column narrowed (240px) and Quick
          Actions slimmed (150px) so the courts region gets more
          horizontal real estate. Court grid col-count now derives
          from the actual court list length. */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)_150px] gap-3" data-testid="ops-grid">
        {/* LEFT — Registration + signed-up + Playing (Iter203).
             Iter226: when a helper undocks, we wrap the same aside in
             an <Rnd> so they can drag + resize it anywhere on screen.
             Both branches render the same IIFE-produced <aside>, only
             the wrapper differs. */}
        {(() => {
          const playingIds = new Set(
            (courts || []).flatMap((c) => [
              ...(c.current_player_ids || []),
              ...(c.next_up_player_ids || []),
            ]),
          );
          const waitingPlayers = allPlayers.filter((p) => !playingIds.has(p.id));
          const playingPlayers = allPlayers.filter((p) => playingIds.has(p.id));
          // Iter361 · Bug 4 — coaching-locked players are already
          // committed to a session and must not appear in the
          // Registration/waiting panel. Their card renders inside the
          // holding-for-coaching view instead.
          const REG_STATUSES = new Set(["imported", "arrived", "resting", "sin_bin", "inactive"]);
          const registrationPlayers = waitingPlayers.filter((p) => REG_STATUSES.has(p.status));
          const signedUpPlayers = waitingPlayers.filter((p) => p.status === "available");
          const regQ = regSearch.trim().toLowerCase();
          const regStatusOk = (p) => (regFilter === "all" ? true : p.status === regFilter);
          const registrationFiltered = registrationPlayers
            .filter((p) => regStatusOk(p))
            .filter((p) => (regQ ? `${p.first_name} ${p.last_name}`.toLowerCase().includes(regQ) : true));
          const registrationSorted = [...registrationFiltered].sort((a, b) => {
            if (regSort === "rating") return (b.current_rating || 0) - (a.current_rating || 0);
            if (regSort === "status") return String(a.status).localeCompare(String(b.status));
            return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          });
          const registrationCounts = registrationPlayers.reduce((acc, p) => {
            acc[p.status] = (acc[p.status] || 0) + 1;
            return acc;
          }, {});
          const useSplit = false;
          const registrationBody = (
          <div data-testid="ops-registration-section">
            <button
              type="button"
              onClick={() => setRegExpanded((v) => !v)}
              className="w-full label-mono text-[#FFB020] mb-2 flex items-center justify-between hover:text-white"
              data-testid="ops-registration-toggle"
            >
              <span className="flex items-center gap-1">
                <UserPlus className="w-3.5 h-3.5" /> Registration ({registrationPlayers.length})
              </span>
              <span className="text-[10px]">{regExpanded ? "▾" : "▸"}</span>
            </button>
            {regExpanded && (
              <>
                <div className="flex flex-wrap gap-1 mb-2 text-[9px] uppercase tracking-wider text-[#525252]" data-testid="ops-registration-counts">
                  {registrationCounts.imported ? <span>Imported · {registrationCounts.imported}</span> : null}
                  {registrationCounts.arrived ? <span className="text-[#66C2FF]">Arrived · {registrationCounts.arrived}</span> : null}
                  {registrationCounts.resting ? <span className="text-[#FFB020]">Resting · {registrationCounts.resting}</span> : null}
                  {registrationCounts.sin_bin ? <span className="text-[#FF4500]">Sin-bin · {registrationCounts.sin_bin}</span> : null}
                  {registrationCounts.inactive ? <span>Inactive · {registrationCounts.inactive}</span> : null}
                </div>
                {registrationPlayers.length > 0 && (
                  <>
                    <input
                      type="text"
                      placeholder="Search registration…"
                      value={regSearch}
                      onChange={(e) => setRegSearch(e.target.value)}
                      data-testid="ops-registration-search"
                      className="w-full text-xs bg-[#0A0A0A] border border-[#262626] px-2 py-1 mb-2 focus:outline-none focus:border-[#FFB020]"
                    />
                    {/* Iter315 — Filter + Sort By restored per spec. */}
                    <div className="flex gap-1 mb-2" data-testid="ops-registration-controls">
                      <select
                        value={regFilter}
                        onChange={(e) => setRegFilter(e.target.value)}
                        data-testid="ops-registration-filter"
                        className="flex-1 min-w-0 text-[10px] uppercase tracking-wider bg-[#0A0A0A] border border-[#262626] px-1.5 py-1 focus:outline-none focus:border-[#FFB020]"
                        title="Filter by status"
                      >
                        <option value="all">Filter: All</option>
                        <option value="imported">Awaiting Check-In</option>
                        <option value="arrived">Checked-In</option>
                        <option value="resting">Resting</option>
                        <option value="sin_bin">Sin-Bin</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <select
                        value={regSort}
                        onChange={(e) => setRegSort(e.target.value)}
                        data-testid="ops-registration-sort"
                        className="flex-1 min-w-0 text-[10px] uppercase tracking-wider bg-[#0A0A0A] border border-[#262626] px-1.5 py-1 focus:outline-none focus:border-[#FFB020]"
                        title="Sort by"
                      >
                        <option value="az">Sort: Name (A-Z)</option>
                        <option value="rating">Sort: Rating (high → low)</option>
                        <option value="status">Sort: Status</option>
                      </select>
                    </div>
                  </>
                )}
                {/* Iter288/289/290 — Single-column registration list.
                     2-col was too tight for names alongside the rating
                     band label + status. Replaced RatingChip with a
                     small coloured DOT so names now fit comfortably.
                     Order: checkbox · dot · name · status · kebab.
                     Iter373 · Fix 4 — Removed the 2xl:grid-cols-2
                     variant. Reg list is always single column now
                     so the courts region gets full width even on
                     ultrawide displays. */}
                <div className="grid grid-cols-1 gap-1" data-testid="ops-registration-grid">
                  {registrationSorted.map((p) => {
                    const active = p.status !== "inactive";
                    // Iter316 — dynamic band colour so preset changes
                    // (e.g. "beginner"/"intermediate") don't blank the dot.
                    const bandColour = bandColourFor(bandColourMap, p.colour_band);
                    return (
                      <div
                        key={p.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, p.id)}
                        onDragEnd={onDragEnd}
                        onContextMenu={(e) => openMenu(e, p)}
                        onTouchStart={(e) => startLongPress(e, p)}
                        onTouchMove={noteMove}
                        onTouchEnd={cancelLongPress}
                        onTouchCancel={cancelLongPress}
                        data-testid={`reg-row-${p.id}`}
                        className={`flex items-center gap-1.5 px-2 py-1 border ${active ? "border-[#262626] hover:border-[#FFB020]" : "border-[#141414] opacity-40"} cursor-grab select-none min-w-0`}
                        title={statusHint(p) + " · Tick to mark Available"}
                        onDoubleClick={(e) => { e.preventDefault(); quickAdvance(p); }}
                      >
                        <label className="flex items-center cursor-pointer shrink-0" onClick={(e) => e.stopPropagation()} title="Mark Available (moves to Signed-Up)">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => setStatus(p, "available")}
                            data-testid={`reg-tick-${p.id}`}
                            className="w-3.5 h-3.5 accent-[#CCFF00]"
                          />
                        </label>
                        <span
                          data-testid={`rating-dot-${p.id}`}
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: bandColour }}
                          title={(p.colour_band || "unrated").toUpperCase()}
                        />
                        {/* Iter315 — Name is the primary column. Give it
                             an explicit min-width so the flex-1 slot never
                             shrinks to 0px on 2-col / narrow-sidebar
                             layouts (bug: previously the status pill's
                             `shrink-0` stole all width, hiding the name
                             behind an ellipsis-of-nothing). */}
                        <span
                          className="flex-1 min-w-[4rem] text-sm font-medium text-white truncate"
                          title={`${p.first_name} ${p.last_name}`}
                          data-testid={`reg-name-${p.id}`}
                        >{p.first_name} {p.last_name}</span>
                        <span className="text-[9px] uppercase tracking-wider text-[#525252] shrink-0">{STATUS_LABELS[p.status] || p.status}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openMenu(e, p); }}
                          data-testid={`reg-kebab-${p.id}`}
                          className="p-0.5 text-[#525252] hover:text-white shrink-0"
                          aria-label="Player options"
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  {registrationPlayers.length === 0 && (
                    <div className="text-xs text-[#525252] italic col-span-full" data-testid="reg-empty">
                      Nobody waiting — import a roster or add a walk-in.
                    </div>
                  )}
                  {registrationPlayers.length > 0 && registrationSorted.length === 0 && (
                    <div className="text-xs text-[#525252] italic col-span-full" data-testid="reg-no-search">
                      No match — try a different search.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          );
          const signedUpBody = (
          <div className={useSplit ? "" : "mt-4 border-t border-[#262626] pt-3"} data-testid="ops-signed-up-section">
            <button
              type="button"
              onClick={toggleSignedUp}
              className="w-full label-mono text-[#CCFF00] mb-2 flex items-center justify-between hover:text-white"
              data-testid="ops-signed-up-toggle"
            >
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" /> Signed-Up ({signedUpPlayers.length})
              </span>
              <span className="text-[10px]">{signedUpExpanded ? "▾" : "▸"}</span>
            </button>
            {signedUpExpanded && (
              <>
            {signedUpPlayers.length > 0 && (
              <div className="text-[10px] text-[#FFB020] mb-2 leading-tight" data-testid="ops-signed-up-hint">
                Every ticked player needs a court — hit Generate Games or Assign All.
              </div>
            )}
            <div className="space-y-1">
              {signedUpPlayers.map((p) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, p.id)}
                  onDragEnd={onDragEnd}
                  onContextMenu={(e) => openMenu(e, p)}
                  onTouchStart={(e) => startLongPress(e, p)}
                  onTouchMove={noteMove}
                  onTouchEnd={cancelLongPress}
                  onTouchCancel={cancelLongPress}
                  data-testid={`player-row-${p.id}`}
                  className="flex items-center gap-2 px-2 py-1 border border-[#262626] hover:border-[#CCFF00] cursor-grab select-none"
                  title={statusHint(p) + " · Untick to move back to Registration"}
                  onDoubleClick={(e) => { e.preventDefault(); quickAdvance(p); }}
                >
                  <label className="flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()} title="Untick to send back to Registration (Arrived)">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => setStatus(p, "arrived")}
                      data-testid={`player-active-${p.id}`}
                      className="w-3.5 h-3.5 accent-[#CCFF00]"
                    />
                  </label>
                  {/* Iter315 — Same min-width guard as the registration
                       row so the name never gets ellipsed away by the
                       trailing chips on narrow layouts. */}
                  <span
                    className="flex-1 min-w-[4rem] text-sm font-medium text-white truncate"
                    title={`${p.first_name} ${p.last_name}`}
                    data-testid={`signed-up-name-${p.id}`}
                  >{p.first_name} {p.last_name}</span>
                  <PartyChip id={p.id} />
                  <TeamChip id={p.id} />
                  <RatingChip p={p} mode={ratingMode} colours={bandColourMap} />
                  <span className="text-[9px] uppercase tracking-wider text-[#CCFF00]">Available</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openMenu(e, p); }}
                    data-testid={`player-kebab-${p.id}`}
                    className="p-0.5 text-[#525252] hover:text-white"
                    aria-label="Player options"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {signedUpPlayers.length === 0 && (
                <div className="text-xs text-[#525252] italic" data-testid="signed-up-empty">
                  Nobody ticked yet — tick players in Registration to fill this queue.
                </div>
              )}
            </div>
              </>
            )}
          </div>
          );
          const playingBody = (
          <>
          {/* Iter200/201 — Collapsible "Playing" tab with search + sort. */}
          {playingPlayers.length > 0 && (() => {
            // Enrich each playing player with their court + elapsed time.
            const now = Date.now();
            const bandOrder = { red: 0, blue: 1, green: 2, spare: 3, unrated: 4 };
            const enriched = playingPlayers.map((p) => {
              let court = null, slot = "", elapsed = 0;
              for (const c of courts || []) {
                if ((c.current_player_ids || []).includes(p.id)) { court = c; slot = "current"; break; }
                if ((c.next_up_player_ids || []).includes(p.id)) { court = c; slot = "next_up"; break; }
              }
              if (court && slot === "current" && court.start_at) {
                try { elapsed = Math.max(0, (now - new Date(court.start_at).getTime()) / 1000); } catch {}
              }
              const courtNum = court ? parseInt((court.name || "").replace(/\D/g, ""), 10) || 0 : 999;
              return { ...p, _court: court, _slot: slot, _elapsed: elapsed, _courtNum: courtNum };
            });
            const q = playingSearch.trim().toLowerCase();
            const filtered = q
              ? enriched.filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q))
              : enriched;
            const sorted = [...filtered].sort((a, b) => {
              if (playingSort === "court") return a._courtNum - b._courtNum;
              if (playingSort === "elapsed") return b._elapsed - a._elapsed;
              if (playingSort === "az") return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
              if (playingSort === "rating") return (b.current_rating || 0) - (a.current_rating || 0);
              if (playingSort === "band") return (bandOrder[a.colour_band || "unrated"] ?? 9) - (bandOrder[b.colour_band || "unrated"] ?? 9);
              return 0;
            });
            const fmtMin = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
            return (
              <details className={`${useSplit ? "" : "mt-4 border-t border-[#262626] pt-3"}`} data-testid="ops-playing-tab" open>
                <summary className="label-mono text-[#66C2FF] cursor-pointer text-[10px] uppercase tracking-wider">
                  Playing ({sorted.length}/{playingPlayers.length}) ▾
                </summary>
                <div className="mt-2 space-y-2">
                  <input
                    type="text"
                    placeholder="Search…"
                    value={playingSearch}
                    onChange={(e) => setPlayingSearch(e.target.value)}
                    data-testid="playing-search"
                    className="w-full text-xs bg-[#0A0A0A] border border-[#262626] px-2 py-1 focus:outline-none focus:border-[#CCFF00]"
                  />
                  <select
                    value={playingSort}
                    onChange={(e) => setPlayingSort(e.target.value)}
                    data-testid="playing-sort"
                    className="w-full text-[10px] uppercase tracking-wider bg-[#0A0A0A] border border-[#262626] px-2 py-1 focus:outline-none focus:border-[#CCFF00]"
                  >
                    <option value="court">Sort: Court #</option>
                    <option value="elapsed">Sort: Elapsed time</option>
                    <option value="az">Sort: A → Z</option>
                    <option value="rating">Sort: Skill rating</option>
                    <option value="band">Sort: Colour band</option>
                  </select>
                  <div className="space-y-1">
                    {sorted.map((p) => (
                      <div
                        key={p.id}
                        data-testid={`playing-row-${p.id}`}
                        title={statusHint(p)}
                        className="flex items-center gap-2 px-2 py-1 text-xs text-[#A3A3A3] hover:bg-[#141414]"
                      >
                        <span className="text-[9px] w-8 text-[#66C2FF]">{p._court?.name?.replace("Court ", "C") || "—"}</span>
                        <span className="flex-1 truncate">{p.first_name} {p.last_name}</span>
                        {p._slot === "current" && p._elapsed > 0 && (
                          <span className="text-[9px] font-mono text-[#525252]" data-testid={`playing-elapsed-${p.id}`}>{fmtMin(p._elapsed)}</span>
                        )}
                        <RatingChip p={p} mode={ratingMode} colours={bandColourMap} />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openMenu(e, p); }}
                          data-testid={`playing-kebab-${p.id}`}
                          className="p-0.5 text-[#525252] hover:text-white"
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    {sorted.length === 0 && (
                      <div className="text-xs text-[#525252] italic" data-testid="playing-empty">No match — try a different search.</div>
                    )}
                  </div>
                </div>
              </details>
            );
          })()}
          </>
          );
          // -------------------------------------------------------------
          // Render: single docked aside (default / mobile) OR three
          // individually draggable Rnd panels (helper undocked, desktop).
          // -------------------------------------------------------------
          if (!useSplit) {
            const sidebarStyle = { width: `${sidebarWidth}px`, overflowAnchor: "none" };
            if (sidebarHeight) sidebarStyle.height = `${sidebarHeight}px`;
            const startWidthDrag = (e) => {
              e.preventDefault();
              const startX = e.clientX;
              const startW = sidebarWidth;
              const move = (ev) => {
                const next = Math.max(240, Math.min(720, startW + (ev.clientX - startX)));
                setSidebarWidth(next);
              };
              const up = () => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", up);
                try { window.localStorage.setItem("ops-sidebar-width", String(sidebarWidth)); } catch {}
              };
              window.addEventListener("mousemove", move);
              window.addEventListener("mouseup", up);
            };
            const startHeightDrag = (e) => {
              e.preventDefault();
              const startY = e.clientY;
              const startH = sidebarHeight || (asideRef.current?.getBoundingClientRect().height || 600);
              const move = (ev) => {
                const next = Math.max(280, startH + (ev.clientY - startY));
                setSidebarHeight(next);
              };
              const up = () => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", up);
                try { window.localStorage.setItem("ops-sidebar-height", String(sidebarHeight)); } catch {}
              };
              window.addEventListener("mousemove", move);
              window.addEventListener("mouseup", up);
            };
            return (
              <aside
                ref={asideRef}
                className={`card-surface p-3 relative z-20 ${sidebarHeight ? "" : "max-h-[calc(100vh-140px)]"} overflow-y-auto shrink-0`}
                data-testid="ops-player-panel"
                style={sidebarStyle}
                onDragOver={onDragOver}
                onDrop={onDropToPool}
              >
                {registrationBody}
                {signedUpBody}
                {playingBody}
                {/* Iter315 — Drag handles. Right edge = width, bottom
                     edge = height. `select-none` + `cursor-*-resize`
                     to signal drag affordance. */}
                <div
                  onMouseDown={startWidthDrag}
                  data-testid="ops-sidebar-resize-x"
                  title="Drag to resize sidebar width"
                  className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-[#CCFF00]/40 select-none"
                />
                <div
                  onMouseDown={startHeightDrag}
                  data-testid="ops-sidebar-resize-y"
                  title="Drag to resize sidebar height"
                  className="absolute bottom-0 left-0 w-full h-1.5 cursor-row-resize hover:bg-[#CCFF00]/40 select-none"
                />
              </aside>
            );
          }
          return null;
        })()}

        {/* CENTER — courts */}
        {/* Iter425/426/427/428 · Visual fix — Left padding on the
             Coaching Scheduler + court grid. Iter428 reduces the
             padding by exactly half the Iter427 step (+2 mobile /
             +4 desktop → -2 / -4). Purely visual — the left panel
             and right column are untouched and court-card sizing is
             unchanged. */}
        <main data-testid="ops-courts-grid" className="min-w-0 pl-12 lg:pl-20">
          {/* Iter274 — Scheduled Coaching panel with live countdowns.
              Auto-hides when no upcoming coaching sessions. */}
          <ScheduledCoachingPanel
            courts={courts}
            players={allPlayers}
            api={api}
            festivalId={id}
            onEdit={(c) => setCoachingModalCourt(c)}
            onCreate={() => {
              // Iter286 — Explicit "+ New Session" entry point. Opens the
              // modal with the first active-status court as the anchor;
              // admin can then reselect any combination in the modal's
              // multi-court chip picker.
              // Iter308 — Regression fix: previously we forwarded the raw
              // court object, so if that court already carried
              // `coaching_group_id` (very common on a live festival) the
              // modal treated it as an EDIT and pre-filled every field.
              // Strip coaching_* fields so `isEdit` resolves to false and
              // the form initialises blank. Overlap validation still runs
              // because it reads from `allCourts` inside the modal.
              const firstAvailable = (courts || []).find((cc) => (cc.status || "active") === "active") || (courts || [])[0];
              if (!firstAvailable) {
                toast.error("Add courts first before scheduling coaching.");
                return;
              }
              const blankAnchor = Object.fromEntries(
                Object.entries(firstAvailable).filter(([k]) => !k.startsWith("coaching_"))
              );
              setCoachingModalCourt(blankAnchor);
            }}
            onChanged={() => load()}
          />
          {/* Iter298 — OPS card content scales with OPS ROWS just like
              the external viewer scales with SCREEN ROWS. Uses CSS
              `zoom` on the grid so fonts, padding, buttons, and drop
              zones all shrink proportionally without touching every
              individual class. Auto/1/2/3 = baseline; 4/5/6 shrink. */}
          {/* Iter299 — At dense OPS ROWS (5-6), the fixed 220px min-column
              forced cards wider than the shrunk container so the last
              column visually spilled behind the sticky Quick Actions
              panel. Drop the hard 220px floor at 5-6 cols and let zoom
              handle legibility; the grid then fits neatly inside its
              center column, next to the QA panel. */}
          <div
            className={opsCols ? "grid gap-3" : "grid gap-3"}
            style={opsCols
              ? {
                  gridTemplateColumns: `repeat(${opsCols}, minmax(${opsCols >= 5 ? "0" : "min(220px, 100%)"}, 1fr))`,
                  zoom: ({ 1: 1.15, 2: 1.05, 3: 1.0, 4: 0.9, 5: 0.8, 6: 0.7 }[opsCols] || 1),
                }
              : {
                  // Iter374 · Fix 1 — Auto mode: fit as many
                  // 220px-min columns as the container allows so
                  // a 12-court festival wraps into multiple rows
                  // instead of getting capped at 4. Wide displays
                  // now spread 5-6+ courts per row automatically.
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                }}
            data-testid="ops-courts-inner-grid"
            data-ops-cols={opsCols || "auto"}
          >
            {courts.map((c) => {
              const isReady = c.state === "ready" || c.state === "pre_start";
              const isLive = c.state === "live";
              // Iter257 — elapsed seconds since the last successful poll.
              // Kept as a live number (recomputed via `tick`) so the
              // countdown ticks smoothly every second instead of the
              // old `tick % 3` bounce.
              // eslint-disable-next-line no-unused-expressions
              tick;
              const elapsedSincePoll = Math.max(0, Math.floor((Date.now() - lastPollAtRef.current) / 1000));
              // Iter204 — when in READY state, show the pre-start clock
              // as the BIG timer (the whole point is that admin sees it).
              // Live shows game remaining; everything else shows the
              // configured game duration as a reference.
              const bigSeconds = isLive
                ? Math.max(0, (c.remaining_seconds || 0) - elapsedSincePoll)
                : isReady
                ? Math.max(0, (c.remaining_seconds || 0) - elapsedSincePoll)
                : (c.game_duration_seconds ?? null);
              const preStartSeconds = isReady
                ? Math.max(0, (c.remaining_seconds || 0) - elapsedSincePoll)
                : null;
              // Iter199 — flash AMBER when a live game has ≤30s left;
              // flash GREEN for 45s after Next-Up gets auto-refilled.
              const amberWarn = isLive && bigSeconds != null && bigSeconds <= 30;
              let greenFlash = false;
              if (c.next_up_filled_at) {
                try {
                  const age = (Date.now() - new Date(c.next_up_filled_at).getTime()) / 1000;
                  greenFlash = age < 45;
                } catch {}
              }
              const flashClass = amberWarn
                ? "animate-pulse border-[#FFB020] ring-2 ring-[#FFB020]"
                : greenFlash
                ? "animate-pulse border-[#4CAF50] ring-2 ring-[#4CAF50]"
                : "";
              return (
              <div key={c.id} data-testid={`court-card-${c.id}`} data-state={c.state} data-status={c.status || "active"} data-manual={c.manual_mode ? "1" : "0"} data-flash={amberWarn ? "amber" : greenFlash ? "green" : ""} className={`border p-3 xl:p-2 relative ${STATE_STYLES[c.state]} ${flashClass} ${(c.status && c.status !== "active") ? "opacity-60" : ""} ${c.manual_mode ? "outline-dashed outline-2 outline-[#66C2FF] -outline-offset-2" : ""}`}>
                {/* Iter261 — Status overlay + change dropdown. */}
                {(c.status && c.status !== "active") && (
                  <div data-testid={`court-status-overlay-${c.id}`} className="absolute inset-x-0 top-6 bg-[#FF4500]/80 text-white text-center text-[11px] uppercase tracking-widest py-1 pointer-events-none font-bold">
                    {c.status === "offline" ? "OFFLINE" : c.status === "maintenance" ? "MAINTENANCE" : "RESERVED · COACHING"}
                  </div>
                )}
                {(c.status === "reserved_coaching") && (() => {
                  // Iter281/283 — Cleaned coaching card. Read-only summary
                  // + drag-drop target (drop players directly onto the
                  // panel to add them to this coaching court). Match-play
                  // controls (timer, HOLDING, drop zones, Next Up,
                  // Auto-fill, Pause, Reset, Game length) are hidden.
                  const sigIds = c.coaching_signup_ids || [];
                  const cur = c.current_player_ids || [];
                  // Iter351 · Bugs 2 & 3 — Merge on-court + registered
                  // signups so drag-drop additions never mask
                  // self-signed players (and vice versa). Currents
                  // first, then any signups not already seated.
                  const displayIds = [
                    ...cur,
                    ...sigIds.filter((pid) => !cur.includes(pid)),
                  ];
                  const displayNames = displayIds.map((pid) => {
                    const p = byId[pid];
                    return p ? `${p.first_name} ${p.last_name}`.trim() : "Player";
                  });
                  const slotMs = c.coaching_slot_at ? new Date(c.coaching_slot_at).getTime() : 0;
                  const durMs = (c.coaching_duration_minutes || 60) * 60000;
                  const now = Date.now();
                  // Iter344 · Bug fix — When the court has an actively
                  // running game (state=live/ready/pre_start), show the
                  // in-progress countdown from `end_at` rather than
                  // the `starts in Xm` message. Prior code compared
                  // only to slotMs which could point to a NEXT
                  // session's slot on the same court, producing
                  // nonsense like "starts in 228 minutes" while a
                  // session was already in progress.
                  const state = (c.state || "").toLowerCase();
                  const isLive = state === "live" || state === "ready" || state === "pre_start";
                  let countdown = "";
                  if (isLive && c.end_at) {
                    const endMs = new Date(c.end_at).getTime();
                    const s = Math.max(0, Math.round((endMs - now) / 1000));
                    const m = Math.floor(s / 60);
                    countdown = m > 0 ? `${m}m left` : `${s}s left`;
                  } else if (slotMs && now < slotMs) {
                    const s = Math.max(0, Math.round((slotMs - now) / 1000));
                    const m = Math.floor(s / 60);
                    countdown = m > 0 ? `starts in ${m}m` : `starts in ${s}s`;
                  } else if (slotMs && now < slotMs + durMs) {
                    const s = Math.max(0, Math.round((slotMs + durMs - now) / 1000));
                    const m = Math.floor(s / 60);
                    countdown = m > 0 ? `${m}m left` : `${s}s left`;
                  }
                  const startLabel = slotMs ? new Date(slotMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
                  const levelLabel = c.coaching_level === "u35" ? "U3.5" : c.coaching_level === "o35" ? "O3.5" : "";
                  const isMaster = c.coaching_master_court_id && c.coaching_master_court_id === c.id;
                  return (
                    <div
                      data-testid={`court-coaching-panel-${c.id}`}
                      onDragOver={onDragOver}
                      onDrop={(e) => onDrop(e, c.id, "current")}
                      className="mt-2 border-2 border-[#CCFF00] bg-black p-2 space-y-1 text-[10px]"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="uppercase tracking-widest text-[#CCFF00] font-bold">
                          {isMaster ? "★ Coaching · Master" : c.coaching_group_id ? "Coaching · Slave" : "Coaching"}
                        </span>
                        {/* Iter348 · Bug 1 — Hide Edit once the session
                             is in progress (players seated or state is
                             live/ready/pre_start). Cancel / return-to-
                             Active are still available via the top-row
                             status <select> and the Scheduled Coaching
                             panel controls. */}
                        {!(isLive || cur.length > 0) && (
                          <button type="button" onClick={() => setCoachingModalCourt(c)} data-testid={`coaching-edit-${c.id}`} className="text-[9px] px-1.5 py-0.5 border border-[#525252] text-[#A3A3A3] uppercase tracking-wider hover:border-[#CCFF00] hover:text-[#CCFF00]">Edit</button>
                        )}
                      </div>
                      <div className="text-white font-bold truncate" data-testid={`coaching-session-name-${c.id}`}>
                        {c.coaching_session_name || "Untitled session"}
                        {levelLabel && <span className="ml-1 text-[9px] text-[#A3A3A3]">· {levelLabel}</span>}
                      </div>
                      {c.coaching_coach_name && (
                        <div className="text-[#A3A3A3]" data-testid={`coaching-coach-name-${c.id}`}>Coach {c.coaching_coach_name}</div>
                      )}
                      {startLabel && (
                        <div className="text-[#A3A3A3] flex items-center justify-between">
                          <span>Start: {startLabel}</span>
                          {countdown && <span className="text-[#FFB020]" data-testid={`coaching-countdown-${c.id}`}>{countdown}</span>}
                        </div>
                      )}
                      <div className="text-[#A3A3A3]" data-testid={`coaching-count-${c.id}`}>
                        {cur.length > 0 ? "On court" : "Registered"}: {displayIds.length}/{c.coaching_max_players || 6}
                      </div>
                      <div className="border border-dashed border-[#CCFF00]/50 p-1 min-h-[46px] text-[10px] text-white/90" data-testid={`coaching-players-${c.id}`}>
                        {displayNames.length === 0 ? (
                          <div className="text-[#66C2FF] italic text-center pt-1">Drop players here to add to session</div>
                        ) : (
                          <ul className="leading-tight space-y-0.5">
                            {displayIds.map((pid, i) => {
                              const p = byId[pid];
                              const nm = p ? `${p.first_name} ${p.last_name}`.trim() : "Player";
                              return (
                                <li key={pid || i} draggable={!!p} onDragStart={(e) => onDragStart(e, pid)} onDragEnd={onDragEnd} className="truncate cursor-grab">· {nm}</li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                      {/* Iter408/409 · Fix — Reset dropdown for
                           reserved_coaching courts. The regular
                           Reset dropdown (line ~2702) is nested
                           inside the `status !== "reserved_coaching"`
                           wrapper (line ~2569), so it never renders
                           here. This standalone dropdown restores a
                           Reset control on every coaching court and
                           applies the user's SOLE condition —
                           `status === "reserved_coaching"` — to
                           swap Clear Court for Finish Coaching
                           Early. Non-coaching courts continue to use
                           the regular dropdown with Clear Court
                           unchanged. */}
                      <select
                        data-testid={`court-reset-${c.id}`}
                        defaultValue=""
                        onChange={(e) => {
                          const mode = e.target.value;
                          e.target.value = "";
                          if (!mode) return;
                          if (mode === "times") {
                            call(() => api.post(`/festival/${id}/courts/${c.id}/reset`, { mode: "times" }), `${c.name} — timers reset`);
                            return;
                          }
                          if (mode === "finish_coaching") {
                            if (!window.confirm(
                              `Finish the coaching session on ${c.name} early?\n\n` +
                              `Players will be released to Resting. All session data ` +
                              `(players, duration, coach) is retained for reporting.`
                            )) return;
                            call(
                              () => api.post(`/festival/${id}/courts/${c.id}/finish-coaching-early`, {}),
                              `${c.name} — coaching finished early`,
                            );
                            return;
                          }
                          if (mode === "promote") {
                            const alsoClearNextUp = window.confirm(
                              `Clearing ${c.name}. Do you also want to CLEAR the Next Up queue?\n\n` +
                              `OK   = clear both (players go back to the pool).\n` +
                              `Cancel = promote Next Up onto the empty court now.`
                            );
                            const nextMode = alsoClearNextUp ? "both" : "promote";
                            const msg = alsoClearNextUp
                              ? `${c.name} — court + Next-Up cleared`
                              : `${c.name} — Next-Up promoted`;
                            call(() => api.post(`/festival/${id}/courts/${c.id}/reset`, { mode: nextMode }), msg);
                            return;
                          }
                        }}
                        className="mt-2 w-full text-[10px] uppercase tracking-wider py-1 px-2 border border-[#FF4500] text-[#FF4500] bg-transparent hover:bg-[#FF4500] hover:text-white focus:outline-none appearance-auto"
                        title="Court reset options"
                      >
                        <option value="" className="bg-black">↺ Reset ▾</option>
                        <option value="times" className="bg-black">Reset Times</option>
                        {c.status === "reserved_coaching" ? (
                          <option value="finish_coaching" className="bg-black" data-testid={`court-finish-coaching-opt-${c.id}`}>
                            Finish Coaching Early
                          </option>
                        ) : (
                          <option value="current" className="bg-black">Clear Court</option>
                        )}
                        <option value="promote" className="bg-black">Clear + Next Up…</option>
                      </select>
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between mb-1 gap-1">
                  <span className="text-[10px] xl:text-[9px] uppercase tracking-widest font-bold flex-1 truncate">{c.name}</span>
                  <select
                    value={c.status || "active"}
                    onChange={(e) => {
                      const next = e.target.value;
                      // Iter274 — Flipping to reserved_coaching now opens
                      // the full setup modal instead of a silent inline
                      // patch. The modal writes the status + all coaching
                      // fields together on Save.
                      if (next === "reserved_coaching" && c.status !== "reserved_coaching") {
                        setCoachingModalCourt(c);
                        return;
                      }
                      call(() => api.patch(`/festival/${id}/courts/${c.id}`, { status: next }), `${c.name} · ${next.replace("_", " ")}`);
                    }}
                    data-testid={`court-status-${c.id}`}
                    title="Court status — non-active statuses release players and exclude the court from auto-allocation"
                    className="text-[9px] uppercase bg-transparent border border-[#525252] text-[#A3A3A3] focus:outline-none focus:border-[#CCFF00] px-1 py-0.5"
                  >
                    <option value="active" className="bg-black">Active</option>
                    <option value="offline" className="bg-black">Offline</option>
                    <option value="maintenance" className="bg-black">Maintenance</option>
                    <option value="reserved_coaching" className="bg-black">Reserved · Coaching</option>
                  </select>
                </div>
                {(c.status || "active") === "active" && (
                  <CourtModeToggle court={c} canToggle={role.role !== "check_in_helper"}
                    onToggle={(manual) => call(() => api.patch(`/festival/${id}/courts/${c.id}`, { manual_mode: manual }), `${c.name} · ${manual ? "manual mode" : "auto mode"}`)} />
                )}
                {(c.status !== "reserved_coaching") && (<>
                {/* Iter258 — Top status band. Shows PRE-START label
                    when isReady but the actual countdown lives ONLY on
                    the big timer below (dedupe from iter257). */}
                <div className={`flex items-center justify-between text-[10px] uppercase tracking-widest mb-1 px-1 py-0.5 -mx-1 -mt-1 ${amberWarn ? "bg-[#FFB020] text-black" : greenFlash ? "bg-[#4CAF50] text-black" : isReady ? "bg-[#0F0F0A] text-[#CCFF00]" : "text-[#525252]"}`} data-testid={`court-band-${c.id}`}>
                  <span>
                    {amberWarn ? "◆ ENDING SOON" : greenFlash ? "◉ NEW LINE-UP" : isReady ? "⚡ PRE-START" : c.state.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm xl:text-xs font-semibold">
                    {c.name}
                    {isReady && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest text-[#CCFF00]" data-testid={`court-prestart-label-${c.id}`}>
                        Pre-start
                      </span>
                    )}
                  </div>
                  <div className={`text-2xl xl:text-xl font-mono ${isReady ? "text-[#CCFF00]" : ""}`} data-testid={`court-timer-${c.id}`}>
                    {fmt(bigSeconds)}
                  </div>
                </div>
                {/* Iter204 — game-duration reference under the big clock during PRE-START. */}
                {isReady && (
                  <div className="text-[9px] uppercase tracking-wider text-[#525252] -mt-1 mb-1 text-right" data-testid={`court-game-ref-${c.id}`}>
                    Game will run {fmt(c.game_duration_seconds ?? null)}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-wider mb-2">
                  {isLive ? "● LIVE" : (c.state === "finished" || c.state === "timeout") ? "◆ FINISHED" : isReady ? "⚡ READY — hit Start to override" : (c.current_player_ids || []).length === 4 ? (
                    <span className="text-[#CCFF00]" data-testid={`court-armed-${c.id}`}>◆ AUTO-ARMING</span>
                  ) : c.state === "idle" ? (
                    <span data-testid={`court-holding-${c.id}`}>HOLDING · needs {Math.max(0, 4 - ((c.current_player_ids || []).length))} more</span>
                  ) : c.state === "waiting" || c.state === "assigned" ? (
                    <span data-testid={`court-waiting-${c.id}`}>WAITING · needs {Math.max(0, 4 - ((c.current_player_ids || []).length))} more</span>
                  ) : c.state.toUpperCase()}
                </div>
                <div
                  className="space-y-1 mb-2 xl:mb-1 min-h-[100px] xl:min-h-[76px] border border-dashed border-white/10 p-1"
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, c.id, "current")}
                  data-testid={`court-drop-current-${c.id}`}
                >
                  {(c.current_player_ids || []).length === 0 ? (
                    <div className="text-xs xl:text-[11px] text-[#A3A3A3] italic text-center pt-4">Drop 4 players here</div>
                  ) : (c.current_player_ids || []).map((pid) => {
                    const p = byId[pid];
                    if (!p) return null;
                    return (
                      <div
                        key={pid}
                        draggable
                        onDragStart={(e) => onDragStart(e, pid)}
                        className="flex items-center gap-2 text-xs xl:text-[11px] px-2 xl:px-1.5 py-1 xl:py-0.5 bg-black/40 border border-white/10 cursor-grab"
                      >
                        <span className="flex-1 truncate">{p.first_name} {p.last_name}</span>
                        <RatingChip p={p} mode={ratingMode} colours={bandColourMap} />
                      </div>
                    );
                  })}
                </div>
                <div
                  className="pt-1 border-t border-white/10 min-h-[60px] xl:min-h-[44px]"
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, c.id, "next_up")}
                  data-testid={`court-drop-nextup-${c.id}`}
                >
                  <div className="text-[9px] uppercase tracking-wider text-[#A3A3A3] mb-1">Next Up (drop here)</div>
                  <div className="text-[11px] xl:text-[10px] space-y-0.5">
                    {(c.next_up_player_ids || []).map((pid) => {
                      const p = byId[pid];
                      return p ? (
                        <div key={pid} draggable onDragStart={(e) => onDragStart(e, pid)} onDragEnd={onDragEnd} className="cursor-grab">
                          {p.first_name} {p.last_name}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
                <div className="mt-2 xl:mt-1 flex gap-1 flex-wrap">
                  {c.state === "idle" && !c.manual_mode && (
                    <button type="button" data-testid={`court-assign-${c.id}`} onClick={() => call(() => api.post(`/festival/${id}/courts/${c.id}/assign`, { slot: "current" }), "Filled")} className="flex-1 text-[10px] uppercase tracking-wider py-1 border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black">
                      Auto-fill
                    </button>
                  )}
                  {(c.state === "ready" || c.state === "pre_start") && (
                    <button
                      type="button"
                      data-testid={`court-start-${c.id}`}
                      disabled={(c.current_player_ids || []).length !== 4}
                      title={(c.current_player_ids || []).length !== 4 ? `Need 4 players to start (currently ${(c.current_player_ids || []).length}/4)` : "Start the game now, overriding the pre-start countdown"}
                      onClick={() => call(() => api.post(`/festival/${id}/courts/${c.id}/start`), "Started early")}
                      className="flex-1 text-[10px] uppercase tracking-wider py-1 bg-[#CCFF00] text-black font-bold inline-flex items-center justify-center gap-1 disabled:bg-[#262626] disabled:text-[#525252] disabled:cursor-not-allowed"
                    >
                      <Play className="w-3 h-3" /> {(c.current_player_ids || []).length !== 4 ? `Need 4 (${(c.current_player_ids || []).length}/4)` : "Start Now"}
                    </button>
                  )}
                  {(c.state === "waiting" || c.state === "assigned") && (
                    <button
                      type="button"
                      data-testid={`court-start-${c.id}`}
                      disabled={(c.current_player_ids || []).length !== 4}
                      title={(c.current_player_ids || []).length !== 4 ? `Need 4 players to start (currently ${(c.current_player_ids || []).length}/4)` : "Start the game"}
                      onClick={() => call(() => api.post(`/festival/${id}/courts/${c.id}/start`), "Started")}
                      className="flex-1 text-[10px] uppercase tracking-wider py-1 bg-[#CCFF00] text-black font-bold inline-flex items-center justify-center gap-1 disabled:bg-[#262626] disabled:text-[#525252] disabled:cursor-not-allowed"
                    >
                      <Play className="w-3 h-3" /> {(c.current_player_ids || []).length !== 4 ? `Need 4 (${(c.current_player_ids || []).length}/4)` : "Start"}
                    </button>
                  )}
                  {(c.state === "live" || c.state === "finished" || c.state === "timeout") && (
                    <button type="button" data-testid={`court-end-${c.id}`} onClick={() => call(() => api.post(`/festival/${id}/courts/${c.id}/end`), "Ended")} className="flex-1 text-[10px] uppercase tracking-wider py-1 border border-white text-white hover:bg-white hover:text-black inline-flex items-center justify-center gap-1">
                      <Square className="w-3 h-3" /> End
                    </button>
                  )}
                  {/* Iter210 — Pause / Resume toggle per court. */}
                  {c.paused ? (
                    <button type="button" data-testid={`court-resume-${c.id}`}
                      onClick={() => call(() => api.patch(`/festival/${id}/courts/${c.id}`, { paused: false }), `${c.name} resumed`)}
                      className="text-[10px] uppercase tracking-wider py-1 px-2 border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black inline-flex items-center justify-center gap-1"
                      title="Resume this court">
                      <Play className="w-3 h-3" /> Resume
                    </button>
                  ) : (
                    <button type="button" data-testid={`court-pause-${c.id}`}
                      onClick={() => call(() => api.patch(`/festival/${id}/courts/${c.id}`, { paused: true }), `${c.name} paused`)}
                      className="text-[10px] uppercase tracking-wider py-1 px-2 border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black inline-flex items-center justify-center gap-1"
                      title="Pause this court's clock">
                      <Pause className="w-3 h-3" /> Pause
                    </button>
                  )}
                  {/* Iter260 — Three-way court reset: Times / Clear / Clear+NextUp.
                      Compact <select> menu keeps the card tight while
                      giving admins the granular controls they asked for. */}
                  <select
                    data-testid={`court-reset-${c.id}`}
                    defaultValue=""
                    onChange={(e) => {
                      const mode = e.target.value;
                      e.target.value = ""; // reset picker after action
                      if (!mode) return;
                      if (mode === "times") {
                        call(() => api.post(`/festival/${id}/courts/${c.id}/reset`, { mode: "times" }), `${c.name} — timers reset`);
                        return;
                      }
                      if (mode === "current") {
                        if (!window.confirm(`Are you sure you want to clear all players from ${c.name}?`)) return;
                        call(() => api.post(`/festival/${id}/courts/${c.id}/reset`, { mode: "current" }), `${c.name} — cleared`);
                        return;
                      }
                      if (mode === "promote") {
                        const alsoClearNextUp = window.confirm(
                          `Clearing ${c.name}. Do you also want to CLEAR the Next Up queue?\n\n` +
                          `OK   = clear both (players go back to the pool).\n` +
                          `Cancel = promote Next Up onto the empty court now.`
                        );
                        const nextMode = alsoClearNextUp ? "both" : "promote";
                        const msg = alsoClearNextUp
                          ? `${c.name} — court + Next-Up cleared`
                          : `${c.name} — Next-Up promoted`;
                        call(() => api.post(`/festival/${id}/courts/${c.id}/reset`, { mode: nextMode }), msg);
                        return;
                      }
                    }}
                    className="text-[10px] uppercase tracking-wider py-1 px-2 border border-[#FF4500] text-[#FF4500] bg-transparent hover:bg-[#FF4500] hover:text-white focus:outline-none w-1/2 min-w-0 truncate appearance-auto"
                    title="Court reset options"
                  >
                    <option value="" className="bg-black">↺ Reset ▾</option>
                    <option value="times" className="bg-black">Reset Times</option>
                    <option value="current" className="bg-black">Clear Court</option>
                    <option value="promote" className="bg-black">Clear + Next Up…</option>
                  </select>
                </div>
                {/* Iter210 — Per-court game duration override. */}
                <div className="mt-2 xl:mt-1 flex items-center justify-between text-[10px] xl:text-[9px] uppercase tracking-wider text-[#525252]">
                  <span>Game length</span>
                  <label className="flex items-center gap-1">
                    <input
                      type="number"
                      min={1}
                      max={90}
                      defaultValue={c.duration_min ?? config.court_default_minutes ?? 12}
                      key={`court-min-${c.id}-${c.duration_min ?? config.court_default_minutes}`}
                      onBlur={(e) => setCourtMinutes(c.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      data-testid={`court-duration-${c.id}`}
                      className="w-11 bg-transparent border-b border-[#525252] text-center focus:outline-none focus:border-[#CCFF00]"
                    />
                    <span>min{c.duration_min ? "" : " · default"}</span>
                  </label>
                </div>
                </>)}
              </div>
              );
            })}
          </div>
        </main>

        {/* RIGHT — quick actions + settings (Iter224: hidden for helpers,
            Iter237c: per-button visibility + edit toggle for the owner). */}
        {!isHelper && (
        <aside
          className="card-surface p-2 lg:sticky lg:top-2 lg:self-start lg:max-h-[calc(100vh-1rem)] lg:overflow-y-auto"
          data-testid="ops-quick-actions"
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={toggleQaSection}
              data-testid="ops-quick-actions-toggle"
              className="flex-1 label-mono text-[#CCFF00] flex items-center justify-between hover:text-white pr-2"
            >
              <span>QUICK ACTIONS{!qaExpanded ? ` (${Object.values(qaVisible).filter(Boolean).length})` : ""}</span>
              <span className="text-[10px]">{qaExpanded ? "▾" : "▸"}</span>
            </button>
            {qaExpanded && (
            <button
              type="button"
              onClick={() => setQaEditing((v) => !v)}
              data-testid="qa-edit-toggle"
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${qaEditing ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#525252] hover:text-white"}`}
              title="Show / hide individual Quick Action buttons"
            >
              {qaEditing ? "Done" : "Edit"}
            </button>
            )}
          </div>
          {qaExpanded && (
          <div
            data-testid="ops-quick-actions-body"
            className="[&_button]:!py-1 [&_button]:!text-[10px] [&_button]:!px-1.5 [&_.w-3]:!w-2.5 [&_.h-3]:!h-2.5 [&_.w-3\\.5]:!w-3 [&_.h-3\\.5]:!h-3 [&_.gap-2]:!gap-1 space-y-1 text-[10px]"
          >
          <><div className="space-y-1">
            {(qaVisible.generate || qaEditing) && (
              <div className="relative">
                {qaEditing && (
                  <button type="button" onClick={() => toggleQa("generate")} data-testid="qa-toggle-generate" className={`absolute -left-1 -top-1 z-10 w-4 h-4 border ${qaVisible.generate ? "bg-[#CCFF00] border-[#CCFF00] text-black" : "border-[#525252] text-[#525252]"} text-[9px] leading-none`}>{qaVisible.generate ? "✓" : "✕"}</button>
                )}
                <button type="button" data-testid="qa-generate-games" disabled={qaEditing} onClick={() => call(async () => {
                  const r = await api.post(`/festival/${id}/courts/generate-games`);
                  const { filled_current = 0, filled_next_up = 0, hints = [] } = r.data || {};
                  if (filled_current + filled_next_up === 0 && hints.length > 0) {
                    for (const h of hints) toast.warning(h);
                    throw new Error("__handled__");
                  }
                  return r;
                }, "Generated games")} className={`w-full py-3 text-xs uppercase tracking-wider bg-[#CCFF00] text-black font-bold hover:bg-[#B8E600] inline-flex items-center justify-center gap-2 ${qaEditing && !qaVisible.generate ? "opacity-40" : ""}`}>
                  <Zap className="w-4 h-4" /> Generate Games
                </button>
              </div>
            )}
            {(qaVisible.assign || qaEditing) && (
              <div className="relative">
                {qaEditing && (
                  <button type="button" onClick={() => toggleQa("assign")} data-testid="qa-toggle-assign" className={`absolute -left-1 -top-1 z-10 w-4 h-4 border ${qaVisible.assign ? "bg-[#CCFF00] border-[#CCFF00] text-black" : "border-[#525252] text-[#525252]"} text-[9px] leading-none`}>{qaVisible.assign ? "✓" : "✕"}</button>
                )}
                <button type="button" data-testid="qa-assign-all" disabled={qaEditing} onClick={() => call(() => api.post(`/festival/${id}/courts/assign-all`), "Filled all courts")} className={`w-full py-2 text-[11px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center justify-center gap-2 ${qaEditing && !qaVisible.assign ? "opacity-40" : ""}`}>
                  <Zap className="w-3.5 h-3.5" /> Assign All Courts
                </button>
              </div>
            )}
            {(qaVisible.shuffle || qaEditing) && (
              <div className="relative">
                {qaEditing && (
                  <button type="button" onClick={() => toggleQa("shuffle")} data-testid="qa-toggle-shuffle" className={`absolute -left-1 -top-1 z-10 w-4 h-4 border ${qaVisible.shuffle ? "bg-[#CCFF00] border-[#CCFF00] text-black" : "border-[#525252] text-[#525252]"} text-[9px] leading-none`}>{qaVisible.shuffle ? "✓" : "✕"}</button>
                )}
                <button type="button" data-testid="qa-shuffle" disabled={qaEditing} onClick={() => call(() => api.post(`/festival/${id}/courts/shuffle-next-up`), "Shuffled")} className={`w-full py-2 text-[11px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center justify-center gap-2 ${qaEditing && !qaVisible.shuffle ? "opacity-40" : ""}`}>
                  <Shuffle className="w-3.5 h-3.5" /> Shuffle Next Up
                </button>
              </div>
            )}
            {(qaVisible.pause || qaEditing) && (
              <div className="relative">
                {qaEditing && (
                  <button type="button" onClick={() => toggleQa("pause")} data-testid="qa-toggle-pause" className={`absolute -left-1 -top-1 z-10 w-4 h-4 border ${qaVisible.pause ? "bg-[#CCFF00] border-[#CCFF00] text-black" : "border-[#525252] text-[#525252]"} text-[9px] leading-none`}>{qaVisible.pause ? "✓" : "✕"}</button>
                )}
                <button type="button" data-testid="qa-pause" disabled={qaEditing} onClick={() => call(() => api.post(`/festival/${id}/courts/pause-all`), "Paused")} className={`w-full py-2 text-[11px] uppercase tracking-wider border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black inline-flex items-center justify-center gap-2 ${qaEditing && !qaVisible.pause ? "opacity-40" : ""}`}>
                  <Pause className="w-3.5 h-3.5" /> Pause All
                </button>
              </div>
            )}
            {(qaVisible.resume || qaEditing) && (
              <div className="relative">
                {qaEditing && (
                  <button type="button" onClick={() => toggleQa("resume")} data-testid="qa-toggle-resume" className={`absolute -left-1 -top-1 z-10 w-4 h-4 border ${qaVisible.resume ? "bg-[#CCFF00] border-[#CCFF00] text-black" : "border-[#525252] text-[#525252]"} text-[9px] leading-none`}>{qaVisible.resume ? "✓" : "✕"}</button>
                )}
                <button type="button" data-testid="qa-resume" disabled={qaEditing} onClick={() => call(() => api.post(`/festival/${id}/courts/resume-all`), "Resumed")} className={`w-full py-2 text-[11px] uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black inline-flex items-center justify-center gap-2 ${qaEditing && !qaVisible.resume ? "opacity-40" : ""}`}>
                  <Play className="w-3.5 h-3.5" /> Resume All
                </button>
              </div>
            )}
          </div>
          <div className="mt-4 text-[10px] text-[#525252] leading-relaxed">
            <div className="mb-1 uppercase tracking-wider text-[#A3A3A3] flex items-center gap-1"><Settings2 className="w-3 h-3" /> Tips</div>
            • Drag a player onto any court · drop zone highlights on hover<br />
            • Right-click a player for status shortcuts + Sin-bin<br />
            • Toggle Ratings ↔ Colour Band up top
          </div>
          </>
          </div>
          )}
        </aside>
        )}
      </div>

      {partnerFor && (
        <AssignPartnerModal
          festivalId={id}
          player={partnerFor}
          players={allPlayers}
          playRequests={playRequests}
          onClose={() => setPartnerFor(null)}
          onDone={load}
        />
      )}
      {/* Right-click context menu */}
      {menu && (
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid="player-context-menu"
          style={{ position: "fixed", top: menu.y, left: menu.x, zIndex: 90 }}
          className="bg-[#0A0A0A] border border-[#262626] min-w-[180px] shadow-2xl"
        >
          <div className="px-3 py-2 border-b border-[#262626] text-[10px] uppercase tracking-wider text-[#CCFF00]">
            {menu.player.first_name} {menu.player.last_name}
          </div>
          {config.assignment_mode === "all_in" && !isReadOnly && role.role !== "check_in_helper"
            && canPairPlayer(menu.player, groupedPlayerIds(playRequests)) && (
            <button
              type="button"
              data-testid="menu-assign-partner"
              onClick={() => { setPartnerFor(menu.player); setMenu(null); }}
              className="w-full text-left px-3 py-2 text-[11px] uppercase tracking-wider text-[#CCFF00] hover:bg-[#141414] border-b border-[#262626]"
            >
              Assign partner
            </button>
          )}
          {(() => {
            // Iter344 · New feature — "Assign to Coaching" submenu.
            // Lists every scheduled coaching session for the day
            // (session name, court, time, slots remaining). Selecting
            // one POSTs to the admin coaching_signup endpoint,
            // bypassing self-service. Only rendered when at least
            // one session is scheduled.
            // Iter362 · Bug 2 — Include queue-only sessions
            // (`coaching_slot_queue` entries) so multi-session days
            // don't hide the option once >1 far-future sessions exist.
            const now = Date.now();
            const slotRows = [];
            for (const cc of (courts || [])) {
              const queue = Array.isArray(cc.coaching_slot_queue) ? cc.coaching_slot_queue : [];
              const seenGids = new Set(queue.map((q) => q.coaching_group_id).filter(Boolean));
              const flat = [...queue];
              if (cc.coaching_slot_at && (!cc.coaching_group_id || !seenGids.has(cc.coaching_group_id))) {
                flat.push({
                  coaching_group_id: cc.coaching_group_id,
                  coaching_session_name: cc.coaching_session_name,
                  coaching_slot_at: cc.coaching_slot_at,
                  coaching_duration_minutes: cc.coaching_duration_minutes,
                  coaching_max_players: cc.coaching_max_players,
                  coaching_signup_ids: cc.coaching_signup_ids || [],
                });
              }
              for (const s of flat) {
                if (!s.coaching_slot_at) continue;
                try {
                  const start = new Date(s.coaching_slot_at).getTime();
                  const dur = (s.coaching_duration_minutes || 60) * 60000;
                  if ((start + dur) <= now - 60000) continue;
                } catch { continue; }
                slotRows.push({ slot: s, court: cc });
              }
            }
            if (slotRows.length === 0) return null;
            const groups = new Map();
            for (const { slot, court } of slotRows) {
              const key = slot.coaching_group_id || `solo::${court.id}::${slot.coaching_slot_at}`;
              if (!groups.has(key)) groups.set(key, { slot, courts: [] });
              const g = groups.get(key);
              if (!g.courts.some((cc) => cc.id === court.id)) {
                // Pin the per-court signup array to THIS slot so the
                // capacity meter reflects the correct session, not the
                // court's currently-imminent one.
                const own = (court.coaching_slot_queue || []).find(
                  (q) => q.coaching_group_id === slot.coaching_group_id,
                );
                g.courts.push(own
                  ? { ...court, coaching_signup_ids: own.coaching_signup_ids || [],
                      coaching_max_players: own.coaching_max_players || court.coaching_max_players }
                  : court,
                );
              }
            }
            const sessions = Array.from(groups.entries())
              .map(([k, { slot, courts: list }]) => {
                list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                return { key: k, anchor: { ...list[0], ...slot }, courts: list };
              })
              .sort((a, b) => new Date(a.anchor.coaching_slot_at || 0) - new Date(b.anchor.coaching_slot_at || 0));
            const fmtT = (iso) => {
              try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
              catch { return ""; }
            };
            return (
              <>
                <div className="border-t-2 border-[#262626]" />
                <button
                  type="button"
                  onClick={() => setMenuSub((s) => ({ ...s, coaching: !s.coaching }))}
                  className="w-full flex items-center justify-between px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-wider text-[#CCFF00] bg-[#0F0F0F] hover:bg-[#141414]"
                  data-testid="menu-coaching-header"
                  aria-expanded={menuSub.coaching}
                >
                  <span>Assign to Coaching</span>
                  <span className="text-[10px]">{menuSub.coaching ? "▾" : "▸"}</span>
                </button>
                {menuSub.coaching && (
                <div
                  className="border-l-2 border-[#CCFF00]/40 ml-3"
                  data-testid="menu-coaching-group"
                >
                  {sessions.map(({ key, anchor, courts: linked }) => {
                    const cap = linked.reduce((s, x) => s + (x.coaching_max_players || 6), 0);
                    const used = linked.reduce((s, x) => s + ((x.coaching_signup_ids || []).length), 0);
                    const remaining = Math.max(0, cap - used);
                    const label = anchor.coaching_session_name || "Coaching";
                    const courtLbl = linked.map((x) => x.name).join(" + ");
                    const full = remaining === 0;
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={full}
                        onClick={async () => {
                          const player = menu.player;
                          setMenu(null);
                          try {
                            await api.post(
                              `/festival/${id}/courts/${anchor.id}/coaching/signup`,
                              {
                                player_id: player.id,
                                // Iter363 · Bug 2 — Pass the group id
                                // so the backend can locate a
                                // queue-only far-future session on
                                // the anchor court (top-level
                                // `coaching_slot_at` isn't mirrored
                                // until T-20min).
                                coaching_group_id: anchor.coaching_group_id || null,
                              },
                            );
                            toast.success(`${player.first_name} → ${label}`);
                            await load();
                          } catch (e) {
                            const d = e?.response?.data?.detail;
                            toast.error(
                              typeof d === "string" ? d : (d?.message) || "Couldn't assign",
                            );
                          }
                        }}
                        data-testid={`menu-coaching-${anchor.id}`}
                        className={`w-full text-left pl-4 pr-3 py-1.5 text-xs flex flex-col gap-0.5 hover:bg-[#141414] ${full ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        <span className="text-white font-semibold truncate">{label}</span>
                        <span className="text-[9px] text-[#A3A3A3]">
                          {fmtT(anchor.coaching_slot_at)} · {courtLbl} · {full ? "FULL" : `${remaining} slot${remaining === 1 ? "" : "s"} left`}
                        </span>
                      </button>
                    );
                  })}
                </div>
                )}
                <div className="border-t-2 border-[#262626]" data-testid="menu-coaching-divider" />
              </>
            );
          })()}
          {(() => {
            // Iter196 — First right-click on an imported player shows ONLY
            // Check-in. After check-in, we reveal the 3 desk options
            // (Available / Resting / Sin-bin) and hide Check-in entirely.
            const isImported = menu.player.status === "imported";
            const options = isImported
              ? [{ key: "arrived", label: "Check-in / Arrived" }]
              : [
                  { key: "available", label: "Available / Active" },
                  { key: "resting", label: "Resting" },
                  { key: "sin_bin", label: "Sin-bin" },
                ];
            return options.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  // Iter196 — Check-in flow: after arriving, ask if they're
                  // ready to play right now. Yes → available (eligible for
                  // auto-assign). No → stay arrived (unticked in pool).
                  if (isImported && key === "arrived") {
                    setStatus(menu.player, "arrived");
                    setMenu(null);
                    setTimeout(() => {
                      const ready = window.confirm(
                        `${menu.player.first_name} ${menu.player.last_name} checked in.\n\nReady to play right now?\n\n· OK → mark Available (auto-assign will pick them up)\n· Cancel → stay checked-in but not yet in the pool`,
                      );
                      if (ready) setStatus(menu.player, "available");
                    }, 30);
                    return;
                  }
                  setStatus(menu.player, key);
                  setMenu(null);
                }}
                data-testid={`menu-status-${key}`}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#141414] flex items-center justify-between"
              >
                <span>{label}</span>
                {menu.player.status === key && <span className="text-[#CCFF00]">✓</span>}
              </button>
            ));
          })()}
          <div className="border-t border-[#262626]" />
          <button
            type="button"
            onClick={() => { setEditing(menu.player); setMenu(null); }}
            data-testid="menu-edit"
            className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#141414] flex items-center gap-2 text-[#CCFF00]"
          >
            <Edit2 className="w-3 h-3" /> Edit name / rating
          </button>
          <div className="border-t border-[#262626]" />
          <button
            type="button"
            onClick={() => {
              // Iter221 — Ask admin for the sin-bin duration in minutes.
              const raw = window.prompt("Sin-bin duration (minutes)?", "20");
              if (raw === null) return;
              const mins = Math.max(1, Math.min(180, parseInt(raw, 10) || 20));
              call(() => api.patch(`/festival/${id}/players/${menu.player.id}`, {
                status: "sin_bin", sin_bin_minutes: mins,
              }), `Sin-bin ${mins} min`);
              setMenu(null);
            }}
            data-testid="menu-sin-bin"
            className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#4B0F0F] text-[#FF4500]"
          >
            🚫 Sin Bin (custom min…)
          </button>
          <div className="border-t border-[#262626]" />
          <button
            type="button"
            onClick={() => setMenuSub((s) => ({ ...s, moveTo: !s.moveTo }))}
            className="w-full flex items-center justify-between px-3 py-1 text-[9px] uppercase tracking-wider text-[#525252] hover:text-white hover:bg-[#141414]"
            data-testid="menu-move-header"
            aria-expanded={menuSub.moveTo}
          >
            <span>Move to court</span>
            <span className="text-[10px]">{menuSub.moveTo ? "▾" : "▸"}</span>
          </button>
          {menuSub.moveTo && courts.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => menuPlace(c)}
              data-testid={`menu-move-${c.id}`}
              className="w-full text-left pl-4 pr-3 py-1.5 text-xs hover:bg-[#141414]"
            >
              {c.name}
            </button>
          ))}
          {/* Iter374 · Fix 1 — OPS unassign a player from a coaching
               signup they're currently on. We surface the option for
               every court that has this player in either the top-
               level `coaching_signup_ids` OR a queue-slot signup
               list. Player is returned to Resting on success. */}
          {(() => {
            const pid = menu.player.id;
            const coachingRows = (courts || []).flatMap((c) => {
              const rows = [];
              if ((c.coaching_signup_ids || []).includes(pid)) {
                rows.push({ court: c, gid: c.coaching_group_id || null, slotAt: c.coaching_slot_at });
              }
              for (const s of (c.coaching_slot_queue || [])) {
                if ((s.coaching_signup_ids || []).includes(pid)) {
                  rows.push({ court: c, gid: s.coaching_group_id, slotAt: s.coaching_slot_at, queueOnly: true });
                }
              }
              return rows;
            });
            if (coachingRows.length === 0) return null;
            return (
              <>
                <div className="border-t border-[#262626]" />
                <div className="px-3 py-1 text-[9px] uppercase tracking-wider text-[#525252]">
                  Unassign from coaching
                </div>
                {coachingRows.map((row, i) => (
                  <button
                    key={`${row.court.id}-${row.gid || i}`}
                    type="button"
                    onClick={async () => {
                      const label = row.court.name || "coaching session";
                      try {
                        await api.post(
                          `/festival/${id}/courts/${row.court.id}/coaching/unassign`,
                          { player_id: pid },
                        );
                        toast.success(`Removed from ${label} · Resting`);
                        await load();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Couldn't unassign");
                      } finally {
                        setMenu(null);
                      }
                    }}
                    data-testid={`menu-coaching-unassign-${row.court.id}`}
                    className="w-full text-left pl-4 pr-3 py-1.5 text-xs hover:bg-[#4B0F0F] text-[#FF4500] flex items-center gap-2"
                    title="Remove this player from that coaching session"
                  >
                    ✕ {row.court.name}
                    {row.queueOnly ? <span className="text-[9px] text-[#A3A3A3]">(queued)</span> : null}
                  </button>
                ))}
              </>
            );
          })()}
        </div>
      )}

      {/* Iter184 — Add Player quick modal for walk-ins / volunteers */}
      {showAdd && (
        <QuickPlayerModal
          title="Add walk-in player"
          onClose={() => setShowAdd(false)}
          onSave={async (form) => {
            await call(() => api.post(`/festival/${id}/players`, {
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              imported_rating: form.rating ? Number(form.rating) : null,
              source: form.source, notes: null,
            }), "Player added");
            setShowAdd(false);
          }}
        />
      )}

      {/* Iter184 — Inline name / rating edit modal (from context menu) */}
      {editing && (
        <QuickPlayerModal
          title={`Edit ${editing.first_name} ${editing.last_name}`}
          seed={{ first_name: editing.first_name, last_name: editing.last_name, rating: editing.current_rating ?? "", source: null, email: editing.email || "" }}
          hideSource
          showEmail
          onClose={() => setEditing(null)}
          onSave={async (form) => {
            const patch = { first_name: form.first_name.trim(), last_name: form.last_name.trim() };
            if ((form.email || "").trim() !== (editing.email || "")) patch.email = (form.email || "").trim();
            if (form.rating !== "" && !Number.isNaN(Number(form.rating))) patch.current_rating = Number(form.rating);
            await call(() => api.patch(`/festival/${id}/players/${editing.id}`, patch), "Saved");
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function QuickPlayerModal({ title, seed, hideSource, showEmail, onClose, onSave }) {
  const [form, setForm] = useState(seed || { first_name: "", last_name: "", rating: "", source: "manual" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 p-4" data-testid="quick-player-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-[#0A0A0A] border border-[#262626] p-6">
        <div className="label-mono text-[#CCFF00] mb-2">{title}</div>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="qp-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="First name" className="bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
            <input data-testid="qp-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Last name" className="bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          </div>
          {showEmail && (
            <input data-testid="qp-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="Email (for self-service link)" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          )}
          <input data-testid="qp-rating" type="number" step="0.5" min="0" max="8" value={form.rating} onChange={(e) => set("rating", e.target.value)} placeholder="Rating (e.g. 3.5)" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          {!hideSource && (
            <select data-testid="qp-source" value={form.source} onChange={(e) => set("source", e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]">
              <option value="manual">Walk-in</option>
              <option value="staff">Helper / staff</option>
              <option value="guest">Guest / spare</option>
            </select>
          )}
        </div>
        <div className="pt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
          <button type="button" data-testid="qp-save" disabled={!form.first_name.trim()} onClick={() => onSave(form)} className="px-4 py-1.5 text-xs uppercase tracking-wider font-bold disabled:opacity-50" style={{ background: "#CCFF00", color: "#0A0A0A" }}>Save</button>
        </div>
      </div>
    </div>
  );
}
